import { useCallback, useEffect, useMemo, useState } from "react";
import { useUserStore, workspaceRole } from "@/src/stores/userStore";
import { useNavigate } from "react-router-dom";
import { Content } from "../../components/wrappers/Content";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { useToast } from "../../components/ui/use-toast";
import { useFetch } from "../../lib/useFetch";
import { cn } from "../../lib/utils";
import { fmtMoneyExact as fmtMoney, Stat } from "../Home/shared";
import { LinkDealDialog } from "../Bookings/LinkDealDialog";
import { XeroInvoiceSearchDialog } from "../Bookings/XeroInvoiceSearchDialog";

/**
 * Payments inbox (Phase D, ops parity): two tabs —
 *  To match: unmatched payments with scored booking candidates; Confirm links.
 *  Matched:  linked payments with allocation state; Allocate posts the Xero
 *            Payment against the booking's invoice, coded to a bank account.
 */

type Candidate = {
  bookingId: string | null;
  kind?: "booking" | "deal" | "ca";
  ca?: { hsObjectId: string; sequence: number; outstanding: number; currency: string; yacht: string | null; hasInvoice: boolean; netOfCommission?: boolean; brokerCommission?: number } | null;
  dealStage?: string | null;
  dealAmount?: number | null;
  dealCurrency?: string | null;
  fit?: "deposit" | "full" | null;
  clientName: string | null;
  clientEmail?: string | null;
  yachtName: string | null;
  brand: string | null;
  accountCode: string | null;
  confidence: "strong" | "suggestion" | "weak";
  signals: string[];
  expectedAmount: number | null;
  deltaPct: number | null;
  stage: "new_booking" | "won" | "no_deal" | null;
  hsDealId: string | null;
  willAutoConfirm: boolean;
  noInvoices: boolean;
  owes: { amount: number; currency: string | null; number: string | null; paysInFull: boolean; after?: number; dueDate?: string | null; dueDateMovesTo?: string | null } | null;
  instalment: { index: number; of: number; amount?: number; nextDue: string | null; nextIsFinal?: boolean; isFinal?: boolean } | null;
  target: InvoiceLite | null;
  openInvoices: InvoiceLite[];
  warning: string | null;
  paymentBrand: string | null;
  brandMismatch: { payment: string; target: string } | null;
  currencyMismatch: { payment: string; invoice: string } | null;
};

type InvoiceLite = { invoiceId: string; number: string | null; due: number; total: number; currency: string | null; brand: string | null; dueDate: string | null; xeroUrl: string };

/** The invoice(s) a payment would be posted against — one line each, the target first. */
function InvoiceTargets({ invoices, target, className }: { invoices: InvoiceLite[]; target?: InvoiceLite | null; className?: string }) {
  if (!invoices.length) return null;
  const ordered = target ? [target, ...invoices.filter((i) => i.invoiceId !== target.invoiceId)] : invoices;
  return (
    <span className={cn("block w-full", className)}>
      {ordered.slice(0, 3).map((i) => (
        <span key={i.invoiceId} className={cn("flex flex-wrap items-center gap-1 text-[11px]", target && i.invoiceId === target.invoiceId ? "text-slate-700" : "text-slate-400")}>
          <span>{target && i.invoiceId === target.invoiceId ? "→" : "·"}</span>
          <a href={i.xeroUrl} target="_blank" rel="noreferrer noopener" className="font-medium hover:underline" title="Open in Xero">Invoice {i.number ?? i.invoiceId.slice(0, 8)} ↗</a>
          <span>{fmtMoney(i.due, i.currency ?? "EUR")} due{i.total > i.due + 0.01 ? ` of ${fmtMoney(i.total, i.currency ?? "EUR")}` : ""}</span>
          {i.brand && <BrandBadge brand={i.brand} />}
          {i.dueDate && <span>· due {fmtDate(i.dueDate)}</span>}
        </span>
      ))}
      {ordered.length > 3 && <span className="text-[11px] text-slate-400">+{ordered.length - 3} more</span>}
    </span>
  );
}

/** What approving does (ops parity): stage pill + the consequence in one line. */
function Outcome({ c, currency, onLinkDeal, onFindInvoice, receipt, operatorRefund }: { c: Candidate; currency: string | null; onLinkDeal?: () => void; onFindInvoice?: () => void; receipt?: { on: boolean; toggle: () => void }; operatorRefund?: { on: boolean; toggle: () => void } }) {
  const ord = (n: number) => `${n}${["th", "st", "nd", "rd"][((n % 100) - 20) % 10] ?? ["th", "st", "nd", "rd"][n % 100] ?? "th"}`;
  return (
    <span className="mt-1 flex flex-wrap items-center gap-1">
      {c.kind === "ca" ? <span className="rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-medium text-teal-800" title="A central-agent booking on HELM's own fleet — the retail broker's or client's instalment">CA · {c.ca?.yacht ?? "fleet"}</span>
        : c.kind === "deal" ? <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-800" title="A HubSpot deal with no booking yet">{c.dealStage ?? "Contract out"} · no booking yet</span>
        : c.stage === "new_booking" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">New booking</span>}
      {c.stage === "won" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">Won — payment received</span>}
      {c.stage === "no_deal" && (
        <button type="button" onClick={onLinkDeal} className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-medium text-red-800 hover:bg-red-200" title="The booking was created without a HubSpot deal, so it can't be confirmed or invoiced until one is linked — click to find and link it">
          No HubSpot deal · link…
        </button>
      )}
      {c.willAutoConfirm && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">⚡ Will auto-confirm on approve</span>}
      {c.stage === "won" && !c.willAutoConfirm && c.instalment && c.instalment.index > 1 && <span className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[10px] font-medium text-fuchsia-700">↻ Subsequent payment</span>}
      {c.brandMismatch && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800" title={`The money landed in ${c.brandMismatch.payment.toUpperCase()}'s accounts but the invoice is in ${c.brandMismatch.target.toUpperCase()}'s Xero — Confirm lets you choose where to post it`}>
          ⚠ Brand mismatch {c.brandMismatch.payment.toUpperCase()} ≠ {c.brandMismatch.target.toUpperCase()}
        </span>
      )}
      {c.currencyMismatch && (
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800" title={`Payment in ${c.currencyMismatch.payment}, invoice in ${c.currencyMismatch.invoice} — Confirm lets you set the amount to post in ${c.currencyMismatch.invoice}; Xero books the FX difference`}>
          ⚠ Currency {c.currencyMismatch.payment} ≠ {c.currencyMismatch.invoice}
        </span>
      )}
      <span className="block w-full text-[11px]">
        {receipt && c.kind !== "deal" && c.bookingId && (
          <span className={cn("block", receipt.on ? "text-slate-600" : "text-slate-400")} title="Ops' receipt tick box: the client's payment receipt (booking-confirmed for a first payment, payment-received after) goes out from the booking the moment the money lands on it, and is logged on the payment. Untick for a refund, an internal transfer or a payment the client was already receipted for.">
            <label className="inline-flex cursor-pointer items-center gap-1"><input type="checkbox" checked={receipt.on} onChange={receipt.toggle} className="h-3 w-3" />✉ {receipt.on ? `Receipt emailed to ${c.clientEmail ?? "the client"} on approve` : "No receipt"}{receipt.on && !c.clientEmail ? " — no email on the booking, nothing will send" : ""}</label>
          </span>
        )}
        {operatorRefund && c.kind !== "deal" && c.kind !== "ca" && c.bookingId && (
          <span className={cn("block", operatorRefund.on ? "text-red-700" : "text-slate-400")} title="Money sent back by the OPERATOR (a refund after a charter issue), not the client paying: recorded as a refund on the operator side, no receipt, R-code invoice to the operator in Xero paid by this line.">
            <label className="inline-flex cursor-pointer items-center gap-1"><input type="checkbox" checked={operatorRefund.on} onChange={operatorRefund.toggle} className="h-3 w-3" />↩ {operatorRefund.on ? "Refund FROM the operator — recorded on the operator side, no receipt" : "This is a refund from the operator, not client money"}</label>
          </span>
        )}
        {c.kind === "ca" && c.ca && (
          <span className={cn("block", c.ca.hasInvoice ? "text-teal-700" : "text-amber-700")}>{c.ca.hasInvoice ? (c.ca.netOfCommission ? `✓ Client instalment #${c.ca.sequence} net of the broker's commission (${fmtMoney(c.ca.outstanding, c.ca.currency)} − ${fmtMoney(c.ca.brokerCommission ?? 0, c.ca.currency)}) · Approve marks the instalment received in full and posts the cash on the Xero invoice` : `✓ Client instalment #${c.ca.sequence} · ${fmtMoney(c.ca.outstanding, c.ca.currency)} outstanding · Approve writes the slot to HubSpot and posts the payment on the Xero invoice`) : `Instalment #${c.ca.sequence} · ${fmtMoney(c.ca.outstanding, c.ca.currency)} outstanding — ${c.warning ?? "no Xero invoice yet"}`}</span>
        )}
        {c.kind === "deal" && (
          <span className="block text-violet-700">{c.fit === "deposit" ? "✓ Half the deal — the 50% deposit" : c.fit === "full" ? "✓ The whole deal amount" : "Deal"} · Approve creates the booking and opens Confirm booking (charter account, invoice, schedule, HubSpot stage)</span>
        )}
        {c.instalment && (
          <span className="block text-green-700">
            ✓ {ord(c.instalment.index)} of {c.instalment.of} instalment{c.instalment.of === 1 ? "" : "s"}{c.instalment.amount != null ? ` · ${fmtMoney(c.instalment.amount, currency ?? "EUR")}` : ""}{c.instalment.isFinal ? " · final payment" : ""}
          </span>
        )}
        {c.owes ? (
          <span className={c.owes.paysInFull ? "text-green-700" : "text-slate-600"}>
            {c.willAutoConfirm ? "Approve" : "Confirm"} posts it to {c.owes.number ? `invoice ${c.owes.number}` : "the invoice"}
            {c.owes.paysInFull
              ? " and pays it in full"
              : ` (${fmtMoney(c.owes.amount, c.owes.currency ?? currency ?? "EUR")} → ${fmtMoney(c.owes.after ?? Math.max(0, c.owes.amount), c.owes.currency ?? currency ?? "EUR")} due)`}
            {c.owes.dueDateMovesTo
              ? ` and moves its due date to ${new Date(c.owes.dueDateMovesTo).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}${c.instalment?.nextIsFinal ? " — the final client payment" : " — the next instalment"}`
              : c.instalment?.nextDue && !c.owes.paysInFull
                ? ` · next instalment due ${new Date(c.instalment.nextDue).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                : ""}
          </span>
        ) : c.instalment ? (
          c.instalment.nextDue ? <span className="text-slate-500">next instalment due {new Date(c.instalment.nextDue).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span> : null
        ) : c.noInvoices && !c.willAutoConfirm && c.stage !== "no_deal" ? (
          <button type="button" onClick={onFindInvoice} className="text-amber-700 hover:underline" title="No Xero invoice is linked to this booking, so approving can't pay anything down — search Xero and link the invoice first">
            ⚠ No Xero invoice linked · find in Xero…
          </button>
        ) : c.noInvoices ? (
          <span className="italic text-slate-400">No invoices yet</span>
        ) : null}
      </span>
      <InvoiceTargets invoices={c.openInvoices ?? []} target={c.target} />
      {c.warning && <span className="block w-full text-[11px] font-medium text-red-700">⛔ {c.warning}</span>}
    </span>
  );
}

type InboxPayment = {
  id: string;
  source: string;
  externalId: string;
  amount: number;
  currency: string | null;
  amountGbp: number | null;
  occurredAt: string | null;
  reference: string | null;
  counterparty: string | null;
  status: string | null;
  cardLabel: string | null;
  card?: CardInfo | null;
  /** Bank-feed rows: the Xero bank account the line sits in. */
  bankAccountName?: string | null;
  isRefund: boolean;
  noteCount: number;
  lastNote?: LastNote | null;
  brand: string | null;
  candidates: Candidate[];
  /** A Nuvei settlement batch in a bank account — not a client payment; a transfer in Xero. */
  payout?: { provider: "nuvei"; note: string } | null;
};

/** HELM / ANCHOR pill, same colours as the booking header. */
const BrandBadge = ({ brand, className }: { brand: string | null | undefined; className?: string }) => {
  const b = String(brand ?? "").toLowerCase();
  if (b !== "helm" && b !== "anchor") return null;
  return <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white", b === "anchor" ? "bg-[#e9695e]" : "bg-[#0000dc]", className)}>{b}</span>;
};

type MatchedPayment = {
  id: string;
  source: string;
  amount: number;
  currency: string | null;
  occurredAt: string | null;
  reference: string | null;
  counterparty: string | null;
  bookingId: string;
  clientName: string | null;
  yachtName: string | null;
  brand: string | null;
  accountCode: string | null;
  matchedBy: string | null;
  matchedAt: string | null;
  matchMethod: string | null;
  allocated: boolean;
  xeroPaymentId: string | null;
  xeroPaymentAt: string | null;
  xeroPaymentError: string | null;
  /** The booking has no live client invoice on the index — nothing to allocate against until one is found in Xero. */
  noInvoice: boolean;
  /** …and nothing has confirmed its deal yet: Confirm booking raises the charter account + invoice. */
  canConfirm?: boolean;
  /** Ops flagged the money as already posted in Xero — Check Xero stamps the match from the invoice's payments. */
  opsSaysInXero?: boolean;
  hsDealId?: string | null;
  canUnmatch: boolean;
  canChangeMatch: boolean;
  canUnallocate: boolean;
  /** Allocated, but the invoice still shows a small balance (short payment) — Bank Fees write-off offered. */
  residual?: { invoiceId: string; invoiceNumber: string | null; amountDue: number; currency: string | null } | null;
  noteCount: number;
  status: string | null;
  cardLabel: string | null;
  card?: CardInfo | null;
  /** Bank-feed rows: the Xero bank account the line sits in. */
  bankAccountName?: string | null;
};

type ReviewedPayment = {
  id: string;
  source: string;
  amount: number;
  currency: string | null;
  occurredAt: string | null;
  reference: string | null;
  counterparty: string | null;
  reviewStatus: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  xeroPaymentId: string | null;
  noteCount: number;
  status: string | null;
  cardLabel: string | null;
  card?: CardInfo | null;
  /** Bank-feed rows: the Xero bank account the line sits in. */
  bankAccountName?: string | null;
};

type BookingHit = {
  /** "deal": invoices in Xero under a deal with no booking row yet — selecting adopts the deal as a booking first. "ca": a central-agent booking on HELM's fleet — selecting records the next client instalment on it. */
  kind?: "booking" | "deal" | "ca";
  ca?: { hsObjectId: string; sequence: number; outstanding: number; of: number; currency: string; yacht: string | null; hasInvoice: boolean; broker: string | null; netOfCommission?: boolean; brokerCommission?: number } | null;
  hsDealId?: string | null;
  /** HubSpot deal stage label, and the ops ticket stage for bookings. */
  dealStage?: string | null;
  ticketStage?: string | null;
  dealAmount?: number | null;
  dealCurrency?: string | null;
  fit?: "deposit" | "full" | null;
  bookingId: string | null;
  clientName: string | null;
  clientEmail: string | null;
  yachtName: string | null;
  dealName: string | null;
  brand: string | null;
  accountCode: string | null;
  hubspotId: string | null;
  startDate: string | null;
  matchedOn: string | null;
  invoices: number;
  openInvoices: InvoiceLite[];
};

type AllocationOptions = {
  payment: { id: string; amount: number; currency: string | null; reference: string | null; source: string };
  /** The Xero org the payment is posted to — the org holding the invoice, which can differ from the booking's brand. */
  brand: string;
  bookingBrand?: string;
  brandNote?: string | null;
  /** Where the money belongs, per the routing (bank line's own account / ops rule / currency default). */
  suggestedAccount?: { accountId: string; name: string; how: "bank_feed" | "card_rule" | "routing_rule" | "currency_default" | "heuristic" } | null;
  paymentBrand?: string | null;
  brandMismatch?: { payment: string; target: string } | null;
  orgs?: { helm: number; anchor: number };
  invoices: {
    invoiceId: string;
    brand?: string | null;
    crossCurrency?: { invoiceCurrency: string; suggestedAmount: number; closes: boolean; converted: number | null; spotRate: number | null; noRate: boolean } | null;
    invoiceNumber: string | null;
    amountDue: number;
    amountTotal: number;
    currency: string | null;
    dueDate: string | null;
    category: string | null;
  }[];
  bankAccounts: { accountId: string; code: string | null; name: string; currency: string | null }[];
};

type InboxData = { payments: InboxPayment[]; totalGbp: number; strongCount: number };

const SOURCE_LABEL: Record<string, string> = {
  nuvei: "Nuvei card",
  revolut_api: "Revolut",
  revolut: "Revolut",
  barclays: "Barclays",
  hsbc: "HSBC",
};
const SOURCE_PILL: Record<string, { label: string; tone: string }> = {
  nuvei: { label: "Nuvei", tone: "bg-emerald-100 text-emerald-800" },
  revolut_api: { label: "Revolut", tone: "bg-slate-800 text-white" },
  revolut: { label: "Revolut", tone: "bg-slate-800 text-white" },
  barclays: { label: "Barclays", tone: "bg-cyan-100 text-cyan-900" },
  hsbc: { label: "HSBC", tone: "bg-red-100 text-red-800" },
};
/** Where the money arrived: the processor or bank, with the exact Xero bank account for bank-feed rows. */
type LastNote = { body: string; authorEmail: string | null; createdAt: string | null; fromOps: boolean };
// The newest note on a row, inline, with a one-line add box — so notes left for each
// other are seen without opening the thread (Simon, 7 Oct). The thread dialog stays.
function QuickNote({ paymentId, last, count, onOpen, onAdded }: { paymentId: string; last?: LastNote | null; count: number; onOpen: () => void; onAdded: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const who = (e: string | null) => (e ? e.split("@")[0] : "ops app");
  const add = () => {
    if (!draft.trim()) return;
    setPosting(true);
    fetch(`/api/finance/payments-inbox/${paymentId}/notes`, "POST", { body: draft.trim() })
      .then(() => { setDraft(""); setOpen(false); onAdded(); })
      .catch((e: any) => toast({ title: "Couldn't add the note", description: e?.message, variant: "destructive" }))
      .finally(() => setPosting(false));
  };
  return (
    <div className="mt-1 max-w-[340px] text-xs">
      {last && (
        <button type="button" className="block w-full rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-left text-amber-900 hover:border-amber-400" onClick={onOpen} title="Open the note thread">
          <span className="line-clamp-2">{last.body}</span>
          <span className="block text-[10px] text-amber-700">{who(last.authorEmail)}{last.fromOps ? " · ops app" : ""} · {fmtDate(last.createdAt)}{count > 1 ? ` · ${count} notes` : ""}</span>
        </button>
      )}
      {open ? (
        <form className="mt-1 flex gap-1" onSubmit={(e) => { e.preventDefault(); add(); }}>
          <input autoFocus value={draft} maxLength={2000} onChange={(e) => setDraft(e.target.value)} placeholder="Note for the team…" className="min-w-0 flex-1 rounded border border-slate-200 px-2 py-1 outline-none focus:border-blue-400" onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }} />
          <button type="submit" disabled={posting || !draft.trim()} className="rounded bg-slate-800 px-2 py-1 text-white disabled:opacity-50">{posting ? "…" : "Add"}</button>
        </form>
      ) : (
        <button type="button" className="mt-0.5 text-slate-400 hover:text-slate-700 hover:underline" onClick={() => setOpen(true)}>+ note</button>
      )}
    </div>
  );
}

function SourcePill({ source, bankAccountName, payout, className }: { source: string; bankAccountName?: string | null; payout?: { provider: string; note: string } | null; className?: string }) {
  const p = payout ? { label: "Nuvei payout", tone: "bg-emerald-100 text-emerald-800" } : SOURCE_PILL[source] ?? { label: source, tone: "bg-slate-100 text-slate-700" };
  const via = payout ? `via ${SOURCE_PILL[source]?.label ?? source}${bankAccountName ? ` · ${bankAccountName.replace(/^(Revolut|Barclays|HSBC|Nuvei)\s*-?\s*/i, "")}` : ""}` : bankAccountName ? `· ${bankAccountName.replace(/^(Revolut|Barclays|HSBC|Nuvei)\s*-?\s*/i, "")}` : null;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium", p.tone, className)} title={payout?.note ?? bankAccountName ?? SOURCE_LABEL[source] ?? source}>
      {p.label}
      {via && <span className="font-normal opacity-80">{via}</span>}
    </span>
  );
}

const CONF_STYLE: Record<Candidate["confidence"], string> = {
  strong: "bg-green-100 text-green-800",
  suggestion: "bg-amber-100 text-amber-800",
  weak: "bg-slate-100 text-slate-600",
};

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—";

/** Nuvei transaction status + card, shown under the amount. */
type CardInfo = { network: "visa" | "mastercard" | "amex" | "other" | null; wallet: "applepay" | "googlepay" | null; last4: string | null; label: string | null };
const NETWORK_NAME: Record<string, string> = { visa: "Visa", mastercard: "Mastercard", amex: "Amex", other: "Card" };
/** Card network pill: drives which Xero account the payment is coded to (Visa / Mastercard → Nuvei or Ryft; Amex → Revolut Main). */
function CardPill({ card }: { card: CardInfo | null | undefined }) {
  if (!card?.network && !card?.last4) return null;
  const net = card.network ?? "other";
  const tone = net === "amex" ? "bg-sky-100 text-sky-800" : net === "visa" ? "bg-indigo-100 text-indigo-800" : net === "mastercard" ? "bg-orange-100 text-orange-800" : "bg-slate-100 text-slate-600";
  const title = net === "amex" ? "Amex → Revolut Main account in this currency" : net === "other" ? "Card network unknown — routed like Visa / Mastercard" : `${NETWORK_NAME[net]} → Nuvei (Anchor) / Ryft (HELM) account in this currency`;
  return (
    <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", tone)} title={title}>
      {card.wallet === "applepay" ? "Apple Pay · " : card.wallet === "googlepay" ? "Google Pay · " : ""}{NETWORK_NAME[net]}{card.last4 ? ` ·· ${card.last4}` : ""}
    </span>
  );
}

function PaymentMeta({ status, cardLabel, card }: { status: string | null; cardLabel: string | null; card?: CardInfo | null }) {
  if (!status && !cardLabel && !card) return null;
  const s = (status ?? "").toLowerCase();
  const tone =
    s === "approved" || s === "settled" || s === "success"
      ? "bg-green-100 text-green-800"
      : s === "refunded" || s === "chargeback" || s === "voided"
        ? "bg-red-100 text-red-700"
        : s === "pending"
          ? "bg-amber-100 text-amber-800"
          : "bg-slate-100 text-slate-600";
  return (
    <span className="mt-0.5 flex items-center gap-1.5 text-xs">
      {status && <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", tone)}>{status}</span>}
      {card?.network || card?.last4 ? <CardPill card={card} /> : cardLabel && <span className="text-slate-400">{cardLabel}</span>}
    </span>
  );
}

/** Brokers see the inbox and the booking pages behind it (ops parity: read-only); payment actions are ops / finance / admin. The server enforces this — the hook just keeps the controls out of sight. */
const useReadOnly = () => workspaceRole(useUserStore((st) => st.user)) === "broker";

export default function PaymentsInbox() {
  const { fetch } = useFetch();
  const readOnly = useReadOnly();
  const { toast } = useToast();
  const [tab, setTab] = useState<"to_match" | "matched" | "reviewed">("to_match");
  const [data, setData] = useState<InboxData | null>(null);
  const [matched, setMatched] = useState<MatchedPayment[] | null>(null);
  const [reviewed, setReviewed] = useState<ReviewedPayment[] | null>(null);

  const load = useCallback(() => {
    fetch<{ data: InboxData }>("/api/finance/payments-inbox")
      .then((r) => setData(r.data))
      .catch(() => setData({ payments: [], totalGbp: 0, strongCount: 0 }));
    fetch<{ data: MatchedPayment[] }>("/api/finance/payments-inbox/matched")
      .then((r) => setMatched(r.data ?? []))
      .catch(() => setMatched([]));
    fetch<{ data: ReviewedPayment[] }>("/api/finance/payments-inbox/reviewed")
      .then((r) => setReviewed(r.data ?? []))
      .catch(() => setReviewed([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  return (
    <Content title="Payments inbox">
      <div className="flex flex-col gap-4 p-1">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat
            label="Awaiting match"
            value={data?.payments.length}
            detail={data ? `${fmtMoney(data.totalGbp, "GBP")} unmatched` : undefined}
            tone={data && data.payments.length ? "bad" : "good"}
          />
          <Stat label="Strong matches" value={data?.strongCount} detail="ready to confirm" tone="good" />
          <Stat
            label="Matched, unallocated"
            value={matched ? matched.filter((m) => !m.allocated).length : undefined}
            detail="need a Xero payment"
            tone={matched && matched.some((m) => !m.allocated) ? undefined : "good"}
          />
          <Stat
            label="Allocated"
            value={matched ? matched.filter((m) => m.allocated).length : undefined}
            detail="written to Xero"
            tone="good"
          />
        </div>

        {readOnly && <p className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-600">Read-only view — you can see every payment and open its booking; matching, allocating and confirming are done by the ops team.</p>}
        <div className="flex items-center gap-1 border-b">
          {(
            [
              ["to_match", `To match${data ? ` (${data.payments.length})` : ""}`],
              ["matched", `Matched${matched ? ` (${matched.length})` : ""}`],
              ["reviewed", `Dismissed${reviewed ? ` (${reviewed.length})` : ""}`],
            ] as ["to_match" | "matched" | "reviewed", string][]
          ).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={cn(
                "-mb-px border-b-2 px-4 py-2 text-sm font-medium",
                tab === k ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "to_match" ? (
          <ToMatchTab data={data} setData={setData} onChanged={load} />
        ) : tab === "matched" ? (
          <MatchedTab matched={matched} onChanged={load} />
        ) : (
          <ReviewedTab reviewed={reviewed} onChanged={load} />
        )}
      </div>
    </Content>
  );
}

/* ---------------------------- To match tab ---------------------------- */

function ToMatchTab({
  data,
  setData,
  onChanged,
}: {
  data: InboxData | null;
  setData: React.Dispatch<React.SetStateAction<InboxData | null>>;
  onChanged: () => void;
}) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const readOnly = useReadOnly();
  const [source, setSource] = useState("");
  const [onlyMatched, setOnlyMatched] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [approving, setApproving] = useState<Record<string, boolean>>({});
  // Rows where the person unticked the automatic receipt (on by default, ops parity).
  const [noReceipt, setNoReceipt] = useState<Set<string>>(new Set());
  const [opRefund, setOpRefund] = useState<Set<string>>(new Set());
  const [confirmFor, setConfirmFor] = useState<InboxPayment | null>(null);
  const [confirmDeal, setConfirmDeal] = useState<string | null>(null);
  const [linkDealFor, setLinkDealFor] = useState<{ bookingId: string; label: string | null; query: string | null } | null>(null);
  const [findInvoiceFor, setFindInvoiceFor] = useState<{ bookingId: string; label: string | null; brand: string | null; query: string | null } | null>(null);
  // A brand / currency mismatch: Confirm matches the payment, then hands the
  // person the Allocate dialog to choose the org, account and amount (ops parity).
  const [allocateFor, setAllocateFor] = useState<MatchedPayment | null>(null);
  const [dismissFor, setDismissFor] = useState<InboxPayment | null>(null);
  // A Nuvei settlement batch: acknowledge it as the Xero transfer it is (ops' "already in Xero").
  const markTransfer = (p: InboxPayment) => {
    setApproving((a) => ({ ...a, [p.id]: true }));
    fetch(`/api/finance/payments-inbox/${p.id}/dismiss`, "POST", { reason: "already_in_xero", note: "Nuvei settlement batch — transfer from the Nuvei clearing account, not a client payment." })
      .then(() => { setData((d) => (d ? { ...d, payments: d.payments.filter((x) => x.id !== p.id) } : d)); toast({ title: "Marked as transfer", description: "The Nuvei payout moved to the Dismissed tab (already in Xero)." }); onChanged(); })
      .catch((e: any) => toast({ title: "Couldn't mark it", description: e?.message, variant: "destructive" }))
      .finally(() => setApproving((a) => { const n = { ...a }; delete n[p.id]; return n; }));
  };
  const [matchFor, setMatchFor] = useState<InboxPayment | null>(null);
  const [notesFor, setNotesFor] = useState<InboxPayment | null>(null);
  const [currency, setCurrency] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const approve = (payment: InboxPayment, candidate: Candidate) => {
    if (candidate.kind === "ca" && candidate.ca) {
      const ca = candidate.ca;
      if (!ca.hasInvoice) { toast({ title: "No Xero invoice on the CA booking", description: "Create the Xero records on the CA booking page first, then approve here.", variant: "destructive" }); navigate(`/ca/${ca.hsObjectId}`); return; }
      if (!window.confirm(`Record ${fmtMoney(payment.amount, payment.currency ?? "EUR")} as client instalment #${ca.sequence} on the ${ca.yacht ?? "CA"} booking for ${candidate.clientName ?? "this client"}? HubSpot's slot is updated and the payment posts on the Xero invoice.`)) return;
      setApproving((a) => ({ ...a, [payment.id]: true }));
      fetch(`/api/finance/payments-inbox/${payment.id}/ca-approve`, "POST", { hsObjectId: ca.hsObjectId, sequence: ca.sequence, netOfBrokerCommission: !!ca.netOfCommission })
        .then((r: any) => {
          const d = r?.data ?? {};
          toast(d.xeroPaymentId ? { title: "Recorded on the CA booking", description: `HubSpot slot #${ca.sequence} updated and ${fmtMoney(payment.amount, payment.currency ?? "EUR")} posted on the Xero invoice${d.account ? ` from ${d.account}` : ""}.` } : { title: "Recorded in HubSpot — Xero payment failed", description: d.xeroError ?? "Post it in Xero by hand.", variant: "destructive" });
          setData((dd) => (dd ? { ...dd, payments: dd.payments.filter((x) => x.id !== payment.id) } : dd));
          onChanged();
        })
        .catch((e: any) => toast({ title: "Couldn't record on the CA booking", description: e?.message ?? "", variant: "destructive" }))
        .finally(() => setApproving((a) => ({ ...a, [payment.id]: false })));
      return;
    }
    if (!candidate.bookingId) {
      // A pre-booking deal with no booking: Confirm booking creates it, raises the invoice and posts this payment.
      if (candidate.hsDealId) { setConfirmDeal(candidate.hsDealId); setConfirmFor(payment); }
      return;
    }
    const bookingId: string = candidate.bookingId;
    setApproving((a) => ({ ...a, [payment.id]: true }));
    const asOperatorRefund = opRefund.has(payment.id);
    if (asOperatorRefund && !window.confirm(`Record ${fmtMoney(payment.amount, payment.currency ?? "EUR")} from ${payment.counterparty ?? "the counterparty"} as a REFUND FROM THE OPERATOR on ${candidate.clientName ?? "this booking"}? It comes off the operator side, no receipt is sent, and an R-code invoice to the operator is raised in Xero and paid by this line.`)) return;
    fetch(`/api/finance/payments-inbox/${payment.id}/approve`, "POST", { bookingId, sendReceipt: !asOperatorRefund && !noReceipt.has(payment.id), kind: asOperatorRefund ? "operator_refund" : "payment" })
      .then((r: any) => {
        const x = r?.data?.xero;
        const rc = r?.data?.receipt;
        if (r?.data?.kind === "operator_refund") {
          const or = x?.operatorRefund ?? {};
          toast({ title: "Refund from operator recorded", description: `${fmtMoney(payment.amount, payment.currency ?? "EUR")} off the operator side of ${candidate.clientName ?? "the booking"}${or.invoice ? ` · Xero ${or.invoice.number ?? ""}${or.xeroPaymentId ? " paid" : " raised"}` : ""}${or.xeroError ? ` · Xero: ${or.xeroError}` : ""}. No receipt sent.` });
          setData((d) => (d ? { ...d, payments: d.payments.filter((q) => q.id !== payment.id) } : d));
          onChanged();
          return;
        }
        const receiptNote = rc?.sent ? (rc.redirected ? " Receipt sent (test copy to you)." : ` Receipt emailed to ${rc.to}.`) : rc?.reason && rc.reason !== "not requested" ? ` Receipt not sent: ${rc.reason}.` : "";
        if (x?.mismatch) {
          toast({
            title: x.mismatch === "brand" ? "Matched — choose where to post it" : "Matched — confirm the amount to post",
            description: `${fmtMoney(payment.amount, payment.currency ?? "EUR")} is on ${candidate.clientName ?? "the booking"} but was not posted to Xero: ${x.reason}.`,
          });
          setAllocateFor({
            id: payment.id, source: payment.source, amount: payment.amount, currency: payment.currency, occurredAt: payment.occurredAt, reference: payment.reference, counterparty: payment.counterparty,
            bookingId, clientName: candidate.clientName, yachtName: candidate.yachtName, brand: candidate.brand, accountCode: candidate.accountCode,
            matchedBy: null, matchedAt: null, matchMethod: "manual", allocated: false, xeroPaymentId: null, xeroPaymentAt: null, xeroPaymentError: null, noInvoice: false,
            canUnmatch: true, canChangeMatch: true, canUnallocate: false, noteCount: payment.noteCount, status: payment.status, cardLabel: payment.cardLabel, card: payment.card, bankAccountName: payment.bankAccountName,
          });
        } else if (x?.needsInvoice && x?.canConfirm && x?.hsDealId) {
          toast({
            title: "Matched — the booking has no invoice yet",
            description: `${fmtMoney(payment.amount, payment.currency ?? "EUR")} is on ${candidate.clientName ?? "the booking"}. Confirm the booking to raise its charter account and invoice, and post this payment against it.`,
          });
          setConfirmDeal(x.hsDealId);
          setConfirmFor(payment);
        } else if (x?.needsInvoice) {
          toast({
            title: "Matched, but nothing to pay in Xero",
            description: `${fmtMoney(payment.amount, payment.currency ?? "EUR")} is on ${candidate.clientName ?? "the booking"}, but no Xero invoice is linked to it. Find the invoice in Xero and link it, then allocate from the Matched tab.`,
            variant: "destructive",
          });
          setFindInvoiceFor({ bookingId, label: candidate.clientName, brand: candidate.brand, query: candidate.clientName?.split(/\s+/).pop() ?? null });
        } else {
          toast({
            title: x?.allocated ? "Payment matched and posted to Xero" : "Payment matched",
            description: x?.allocated
              ? `${fmtMoney(payment.amount, payment.currency ?? "EUR")} paid against ${x.invoiceNumber ?? "the invoice"} from ${x.account}.${x.invoiceAmountDue > 0 ? ` ${fmtMoney(x.invoiceAmountDue, payment.currency ?? "EUR")} still due` : ""}${x.dueDateMovedTo ? ` · due date moved to ${fmtDate(x.dueDateMovedTo)} (next instalment)` : ""}${receiptNote}`
              : `${fmtMoney(payment.amount, payment.currency ?? "EUR")} linked to ${candidate.clientName ?? "the booking"} — ${x?.reason ? `not posted to Xero (${x.reason}); ` : ""}allocate it from the Matched tab.${receiptNote}`,
          });
        }
        setData((d) => (d ? { ...d, payments: d.payments.filter((x) => x.id !== payment.id) } : d));
        onChanged();
      })
      .catch((e: any) =>
        toast({ title: "Error", description: e?.message ?? "Failed to match payment.", variant: "destructive" }),
      )
      .finally(() => setApproving((a) => ({ ...a, [payment.id]: false })));
  };

  const sources = useMemo(() => [...new Set((data?.payments ?? []).map((p) => p.source))].sort(), [data]);
  const currencies = useMemo(
    () => [...new Set((data?.payments ?? []).map((p) => (p.currency ?? "").toUpperCase()).filter(Boolean))].sort(),
    [data],
  );

  const visible = useMemo(
    () =>
      (data?.payments ?? []).filter(
        (p) =>
          (!source || p.source === source) &&
          (!currency || (p.currency ?? "").toUpperCase() === currency) &&
          (!amountMin || Math.abs(p.amount) >= Number(amountMin)) &&
          (!amountMax || Math.abs(p.amount) <= Number(amountMax)) &&
          (!dateFrom || (p.occurredAt ?? "") >= dateFrom) &&
          (!dateTo || (p.occurredAt ?? "9999") <= `${dateTo}T23:59:59Z`) &&
          (!onlyMatched || p.candidates.length > 0) &&
          (!search ||
            [p.reference, p.counterparty, p.externalId, ...p.candidates.map((c) => c.clientName)]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(search.toLowerCase()))),
      ),
    [data, source, currency, amountMin, amountMax, dateFrom, dateTo, onlyMatched, search],
  );

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setSource("")}
          className={cn("rounded-full border px-3 py-1.5 text-sm", !source ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50")}
        >
          All sources
        </button>
        {sources.map((s) => (
          <button
            key={s}
            onClick={() => setSource(s)}
            className={cn("rounded-full border px-3 py-1.5 text-sm", source === s ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50")}
          >
            {SOURCE_LABEL[s] ?? s}
          </button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button
          onClick={() => setOnlyMatched((v) => !v)}
          className={cn("rounded-full border px-3 py-1.5 text-sm", onlyMatched ? "border-green-700 bg-green-700 text-white" : "bg-white hover:bg-slate-50")}
        >
          Has candidate
        </button>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reference, payer, client…"
          className="ml-auto w-72 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {currencies.length > 1 && (
          <>
            <button
              onClick={() => setCurrency("")}
              className={cn("rounded-full border px-2.5 py-1 text-xs", !currency ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50")}
            >
              All currencies
            </button>
            {currencies.map((c) => (
              <button
                key={c}
                onClick={() => setCurrency(currency === c ? "" : c)}
                className={cn("rounded-full border px-2.5 py-1 text-xs", currency === c ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50")}
              >
                {c}
              </button>
            ))}
            <span className="mx-1 h-5 w-px bg-slate-200" />
          </>
        )}
        <input type="number" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} placeholder="Min"
          className="w-24 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400" />
        <span className="text-xs text-slate-400">–</span>
        <input type="number" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} placeholder="Max"
          className="w-24 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400" />
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400" />
        <span className="text-xs text-slate-400">to</span>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400" />
        {(currency || amountMin || amountMax || dateFrom || dateTo) && (
          <button
            className="text-xs text-blue-600 hover:underline"
            onClick={() => { setCurrency(""); setAmountMin(""); setAmountMax(""); setDateFrom(""); setDateTo(""); }}
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Received</th>
              <th className="px-3 py-2">Reference / payer</th>
              <th className="px-3 py-2">Suggested match</th>
              <th className="px-3 py-2">Confidence</th>
              <th className="px-3 py-2">Booking</th>
            </tr>
          </thead>
          <tbody>
            {data === null ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Matching payments…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Inbox zero — nothing unmatched.</td></tr>
            ) : (
              visible.map((p) => {
                const top = p.candidates[0];
                return (
                  <tr key={p.id} className={cn("border-t align-top", top?.confidence === "strong" && "border-l-2 border-l-green-500")}>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span className="font-medium tabular-nums">{fmtMoney(p.amount, p.currency ?? "EUR")}</span>
                      {p.isRefund && (
                        <span className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">refund</span>
                      )}
                      <span className="block text-xs text-slate-400">
                        <SourcePill source={p.source} bankAccountName={p.bankAccountName} payout={p.payout} className="mr-1.5" />{fmtDate(p.occurredAt)}
                        <BrandBadge brand={p.brand} className="ml-1.5" />
                      </span>
                      <QuickNote paymentId={p.id} last={p.lastNote} count={p.noteCount} onOpen={() => setNotesFor(p)} onAdded={onChanged} />
                      {/* Bank rows carry the ops review state here ("pending" in this list by definition); only the card gateway's status says anything. */}
                      <PaymentMeta status={p.source === "nuvei" ? p.status : null} cardLabel={p.cardLabel} card={p.card} />
                    </td>
                    <td className="max-w-[280px] px-3 py-2 text-slate-600">
                      <span className="line-clamp-2">{p.reference ?? "—"}</span>
                      {p.counterparty && <span className="block text-xs text-slate-400">{p.counterparty}</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.payout ? (
                        <>
                          <span className="font-medium text-emerald-800">Nuvei settlement batch — not a client payment</span>
                          <span className="block text-xs text-slate-500">{p.payout.note}</span>
                          {!readOnly && <button
                            className="mt-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                            disabled={!!approving[p.id]}
                            onClick={() => markTransfer(p)}
                          >
                            Transfer · already in Xero
                          </button>}
                        </>
                      ) : top ? (
                        <>
                          <span className="font-medium">{top.clientName ?? "—"}</span>
                          <span className="block text-xs text-slate-500">
                            {[top.yachtName, top.accountCode ? `#${top.accountCode}` : null].filter(Boolean).join(" · ")}
                            {top.kind === "deal" && top.dealAmount != null && (
                              <> · {top.fit === "deposit" ? "50% deposit of" : top.fit === "full" ? "full amount of" : "deal"} {fmtMoney(top.dealAmount, top.dealCurrency ?? p.currency ?? "EUR")}{top.dealStage ? ` · ${top.dealStage}` : ""}</>
                            )}
                            {top.kind !== "deal" && top.expectedAmount != null && (
                              top.instalment && Math.abs(top.deltaPct ?? 0) <= 2
                                ? <> · matches the {["th", "1st", "2nd", "3rd"][top.instalment.index] ?? `${top.instalment.index}th`} instalment</>
                                : <> · expected {fmtMoney(top.expectedAmount, p.currency ?? "EUR")}{top.deltaPct ? ` (${top.deltaPct > 0 ? "+" : ""}${top.deltaPct}%)` : ""}</>
                            )}
                          </span>
                          <Outcome c={top} currency={p.currency} receipt={{ on: !noReceipt.has(p.id) && !opRefund.has(p.id), toggle: () => setNoReceipt((s) => { const n = new Set(s); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; }) }} operatorRefund={{ on: opRefund.has(p.id), toggle: () => setOpRefund((s) => { const n = new Set(s); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; }) }} onLinkDeal={() => top.bookingId && setLinkDealFor({ bookingId: top.bookingId, label: top.clientName, query: p.counterparty && p.counterparty.includes("@") ? p.counterparty : top.clientName })} onFindInvoice={() => top.bookingId && setFindInvoiceFor({ bookingId: top.bookingId, label: top.clientName, brand: top.brand, query: top.clientName?.split(/\s+/).pop() ?? null })} />
                          {p.candidates.length > 1 && (
                            <button
                              className="block text-xs text-blue-600 hover:underline"
                              onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                            >
                              {expanded[p.id] ? "hide" : `+${p.candidates.length - 1} other candidate${p.candidates.length > 2 ? "s" : ""}`}
                            </button>
                          )}
                          {expanded[p.id] &&
                            p.candidates.slice(1).map((c) => (
                              <span key={c.bookingId ?? `deal-${c.hsDealId}`} className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                                <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-medium", CONF_STYLE[c.confidence])}>{c.confidence}</span>
                                {c.clientName ?? "—"}{c.yachtName ? ` · ${c.yachtName}` : ""}
                                <button
                                  className="text-blue-600 hover:underline disabled:opacity-50"
                                  disabled={!!approving[p.id]}
                                  onClick={() => approve(p, c)}
                                >
                                  Confirm
                                </button>
                              </span>
                            ))}
                        </>
                      ) : (
                        <span className="text-slate-400">No candidate</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {top && (
                        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", CONF_STYLE[top.confidence])}>
                          {top.confidence}
                          <span className="ml-1 text-[10px] opacity-70">{top.signals.join("+")}</span>
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {readOnly ? (
                        top?.bookingId ? <button className="text-sm text-blue-600 hover:underline" onClick={() => navigate(`/bookings/${top.bookingId}?tab=payments`)}>Open</button> : <span className="text-xs text-slate-400">—</span>
                      ) : (
                      <span className="flex items-center gap-2">
                        {top && (
                          <>
                            <button
                              className={cn(
                                "rounded-md px-2.5 py-1 text-sm font-medium text-white disabled:opacity-50",
                                top.willAutoConfirm ? "bg-violet-600 hover:bg-violet-700" : top.confidence === "strong" ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700",
                              )}
                              disabled={!!approving[p.id]}
                              onClick={() => (top.willAutoConfirm && top.hsDealId ? (setConfirmDeal(top.hsDealId), setConfirmFor(p)) : approve(p, top))}
                              title={top.willAutoConfirm ? "Pre-booking deal with no invoice: confirming creates the Xero contact, charter account and invoice, and posts this payment against it" : undefined}
                            >
                              {approving[p.id] ? "Matching…" : top.kind === "ca" ? "Record on CA booking" : top.willAutoConfirm ? "⚡ Approve & confirm booking" : "Confirm"}
                            </button>
                            {top.bookingId && (
                              <button className="text-sm text-blue-600 hover:underline" onClick={() => navigate(`/bookings/${top.bookingId}?tab=payments`)}>
                                Open
                              </button>
                            )}
                            {top.kind === "ca" && top.ca && (
                              <button className="text-sm text-blue-600 hover:underline" onClick={() => navigate(`/ca/${top.ca!.hsObjectId}?tab=payments`)}>
                                Open
                              </button>
                            )}
                          </>
                        )}
                        <button className="text-sm text-slate-500 hover:text-slate-800 hover:underline" onClick={() => setMatchFor(p)}>
                          Match…
                        </button>
                        <button className="text-sm text-slate-500 hover:text-slate-800 hover:underline" onClick={() => { setConfirmDeal(null); setConfirmFor(p); }}>
                          New booking
                        </button>
                        <button className="text-sm text-slate-400 hover:text-red-600 hover:underline" onClick={() => setDismissFor(p)}>
                          Dismiss
                        </button>
                      </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        Candidates are scored on the charter-account code in the reference, the client surname, and unpaid-instalment
        amounts. After confirming, allocate the payment to Xero from the Matched tab. A deposit on a deal that has no
        booking yet goes through <span className="font-medium">New booking</span> — that creates the Xero charter account
        and invoice, posts the payment, and advances the HubSpot deal.
      </p>
      {linkDealFor && (
        <LinkDealDialog bookingId={linkDealFor.bookingId} bookingLabel={linkDealFor.label} initialQuery={linkDealFor.query} onClose={() => setLinkDealFor(null)} onLinked={() => { setLinkDealFor(null); onChanged(); }} />
      )}
      {findInvoiceFor && (
        <XeroInvoiceSearchDialog bookingId={findInvoiceFor.bookingId} bookingLabel={findInvoiceFor.label} brand={findInvoiceFor.brand} initialQuery={findInvoiceFor.query} onClose={() => setFindInvoiceFor(null)} onLinked={() => { setFindInvoiceFor(null); onChanged(); }} />
      )}
      {allocateFor && (
        <AllocateDialog payment={allocateFor} onClose={() => setAllocateFor(null)} onDone={() => { setAllocateFor(null); onChanged(); }} />
      )}
      {confirmFor && (
        <ConfirmBookingDialog
          payment={confirmFor}
          initialDealId={confirmDeal}
          onClose={() => setConfirmFor(null)}
          onFindInvoice={() => {
            const top = confirmFor.candidates?.[0];
            setConfirmFor(null);
            if (top?.bookingId) setFindInvoiceFor({ bookingId: top.bookingId, label: top.clientName, brand: top.brand, query: top.clientName?.split(/\s+/).pop() ?? null });
          }}
          onDone={() => {
            setData((d) => (d ? { ...d, payments: d.payments.filter((x) => x.id !== confirmFor.id) } : d));
            setConfirmFor(null);
            onChanged();
          }}
        />
      )}
      {dismissFor && (
        <DismissDialog
          payment={dismissFor}
          onClose={() => setDismissFor(null)}
          onDone={() => {
            setData((d) => (d ? { ...d, payments: d.payments.filter((x) => x.id !== dismissFor.id) } : d));
            setDismissFor(null);
            onChanged();
          }}
        />
      )}
      {notesFor && (
        <NotesDialog
          paymentId={notesFor.id}
          label={`${fmtMoney(notesFor.amount, notesFor.currency ?? "EUR")} · ${notesFor.reference ?? notesFor.counterparty ?? "no reference"}`}
          onClose={() => setNotesFor(null)}
          onChanged={onChanged}
        />
      )}
      {matchFor && (
        <BookingSearchDialog
          title="Match payment to a booking"
          subtitle={`${fmtMoney(matchFor.amount, matchFor.currency ?? "EUR")} · ${matchFor.reference ?? matchFor.counterparty ?? "no reference"}`}
          endpoint={`/api/finance/payments-inbox/${matchFor.id}/approve`}
          suggestions={quickSearchTerms(matchFor.reference, matchFor.counterparty)}
          paymentCurrency={matchFor.currency}
          paymentAmount={matchFor.amount}
          onClose={() => setMatchFor(null)}
          onDone={(result, bookingId, hit) => {
            const p = matchFor;
            setData((d) => (d ? { ...d, payments: d.payments.filter((x) => x.id !== p.id) } : d));
            setMatchFor(null);
            // Payments are always carried to an allocation: a match that couldn't post to an
            // invoice opens Allocate (extras / APA, currency, brand), or Find invoice when there is none.
            const x = result?.xero;
            if (x && !x.allocated && bookingId) {
              // A booking nothing has confirmed yet (converted from the contract, deal
              // never invoiced) goes to Confirm booking — ops' crewed win flow.
              if (x.needsInvoice && x.canConfirm && x.hsDealId) { setConfirmDeal(x.hsDealId); setConfirmFor(p); }
              else if (x.needsInvoice) setFindInvoiceFor({ bookingId, label: hit?.clientName ?? null, brand: hit?.brand ?? null, query: hit?.clientName?.split(/\s+/).pop() ?? null });
              else setAllocateFor({
                id: p.id, source: p.source, amount: p.amount, currency: p.currency, occurredAt: p.occurredAt, reference: p.reference, counterparty: p.counterparty,
                bookingId, clientName: hit?.clientName ?? null, yachtName: hit?.yachtName ?? null, brand: hit?.brand ?? null, accountCode: hit?.accountCode ?? null,
                matchedBy: null, matchedAt: null, matchMethod: "manual", allocated: false, xeroPaymentId: null, xeroPaymentAt: null, xeroPaymentError: null, noInvoice: false,
                canUnmatch: true, canChangeMatch: true, canUnallocate: false, noteCount: p.noteCount, status: p.status, cardLabel: p.cardLabel, card: p.card, bankAccountName: p.bankAccountName,
              });
            }
            onChanged();
          }}
        />
      )}
    </>
  );
}

/* ------------------------- Confirm new booking ------------------------- */

type ConfirmCandidate = {
  hsDealId: string;
  helmDealId: string | null;
  dealName: string | null;
  yachtName: string | null;
  brand: string | null;
  charterType: string | null;
  startDate: string | null;
  dealAmount: number | null;
  dealCurrency: string | null;
  stageLabel: string;
  clientName: string | null;
  confidence: "strong" | "suggestion";
  signals: string[];
};

type ConfirmPreview = {
  links?: { deal: string | null; booking: string | null; bookingSlug: string | null };
  deal: {
    hsDealId: string;
    helmDealId: string;
    shortHelmDealId: string;
    dealName: string | null;
    yachtName: string | null;
    stageLabel: string;
    startDate: string | null;
    finishDate: string | null;
    charterType: string;
    category: string;
    currency: string;
  };
  brand: string;
  paymentBrand?: string | null;
  pipelineBrand?: string | null;
  brandWarning: string | null;
  contact: { firstName: string; lastName: string; email: string } | null;
  amounts: { total: number | null; charterFee?: number | null; vat?: number | null; apa?: number | null; delivery?: number | null; currency: string };
  dueDate: string;
  dueDateWarning: string | null;
  accountName: string;
  bankAccounts: { default: { accountId: string; name: string; currency: string | null } | null; candidates: { accountId: string; name: string; currency: string | null }[] };
  payment: { amount: number; currency: string | null; reference: string | null };
  bookingId?: string | null;
  alreadyConfirmedStage?: boolean;
  schedule?: { sequence: number; scheduled_amount: number | null; scheduled_due_date: string | null }[];
  hsSchedule?: { sequence: number; scheduled_amount: number | null; scheduled_due_date: string | null }[];
  bookingSchedule?: { sequence: number; amount: number; dueDate: string | null; status?: string | null }[];
  scheduleSource?: "hubspot" | "booking" | "none";
  files?: { fileId: string; name: string; size: number | null; createdAt: string | null }[];
  filesLive?: boolean;
};
type SchedRow = { sequence: number; amount: number | null; dueDate: string | null };
/** What the confirm dialog needs of a payment — an inbox row or a matched one. */
type PaymentLite = { id: string; amount: number; currency: string | null; reference: string | null; counterparty: string | null };

function ConfirmBookingDialog({ payment, initialDealId, onClose, onDone, onFindInvoice }: { payment: PaymentLite; initialDealId?: string | null; onClose: () => void; onDone: () => void; onFindInvoice?: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [candidates, setCandidates] = useState<ConfirmCandidate[] | null>(null);
  const [manualDealId, setManualDealId] = useState("");
  const [preview, setPreview] = useState<ConfirmPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [components, setComponents] = useState<{ charterFee: string; vat: string; apa: string; delivery: string }>({ charterFee: "", vat: "", apa: "", delivery: "" });
  const [bankAccountId, setBankAccountId] = useState("");
  const [postPayment, setPostPayment] = useState(true);
  const [sendReceipt, setSendReceipt] = useState(true);
  const [confirming, setConfirming] = useState(false);
  // Ops brand rule: which org raises the invoice — the money's brand by default, switchable.
  const [brandChoice, setBrandChoice] = useState<"helm" | "anchor" | null>(null);
  // Client instalments (ops' InstalmentsSection): seeded from HubSpot, else the
  // booking the contract was converted into, overridden by a parsed contract or
  // by hand. Sent on confirm only when touched.
  const [schedule, setSchedule] = useState<SchedRow[]>([]);
  const [scheduleTouched, setScheduleTouched] = useState(false);
  const [contractSchedule, setContractSchedule] = useState<SchedRow[] | null>(null);
  // Contract card: parse a deal document or an upload to seed the figures.
  const [contract, setContract] = useState<{ name: string; template: string | null; note: string | null; parsed?: any } | null>(null);
  // Payment information (ops' win flow / the app's convert flow): the stakeholder we
  // pay, their receiving account, the owner, HELM's commission → net charter fee.
  type PartyForm = { name: string; address: string; vatNumber: string };
  type BankForm = { currency: string; beneficiaryName: string; bank: string; iban: string; swift: string; accountNumber: string; routingNumber: string };
  const emptyParty: PartyForm = { name: "", address: "", vatNumber: "" };
  const [supplier, setSupplier] = useState<PartyForm>(emptyParty);
  const [owner, setOwner] = useState<PartyForm>(emptyParty);
  const [bank, setBank] = useState<BankForm>({ currency: "", beneficiaryName: "", bank: "", iban: "", swift: "", accountNumber: "", routingNumber: "" });
  const [commissionPct, setCommissionPct] = useState<string>("");
  // Credit-card surcharge billed on top (ops parity): % of the deposit or of the full invoice, one extra line.
  const [ccPct, setCcPct] = useState<string>("");
  const [ccBasis, setCcBasis] = useState<"deposit" | "full">("deposit");
  const [showPaymentInfo, setShowPaymentInfo] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    fetch<{ data: ConfirmCandidate[] }>(`/api/finance/payments-inbox/${payment.id}/confirm-candidates`, "GET")
      .then((r) => setCandidates(r.data))
      .catch((e: any) => {
        setCandidates([]);
        setError(e?.message ?? "Failed to load candidate deals.");
      });
    // Opened from "Approve & confirm booking": the candidate's deal is already chosen.
    if (initialDealId) loadPreview(initialDealId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment.id]);

  const loadPreview = (dealId: string, brand: "helm" | "anchor" | null = brandChoice) => {
    if (!dealId.trim()) return;
    setLoadingPreview(true);
    setError(null);
    fetch<{ data: ConfirmPreview }>(
      `/api/finance/payments-inbox/${payment.id}/confirm-preview?dealId=${encodeURIComponent(dealId.trim())}${brand ? `&brand=${brand}` : ""}`,
      "GET",
    )
      .then((r) => {
        setPreview(r.data);
        const a = r.data.amounts;
        setComponents({
          charterFee: a.charterFee != null ? String(a.charterFee) : "",
          vat: a.vat != null ? String(a.vat) : "",
          apa: a.apa != null ? String(a.apa) : "",
          delivery: a.delivery != null ? String(a.delivery) : "",
        });
        setBankAccountId(r.data.bankAccounts.default?.accountId ?? "");
        if (!scheduleTouched) setSchedule((r.data.schedule ?? []).map((x) => ({ sequence: x.sequence, amount: x.scheduled_amount, dueDate: x.scheduled_due_date })));
        if (r.data.deal.category === "crewed" || r.data.deal.category === "gulet") { setShowPaymentInfo(true); setCommissionPct((c) => c || "15"); }
        setBank((b) => ({ ...b, currency: b.currency || r.data.amounts.currency || "" }));
      })
      .catch((e: any) => setError(e?.message ?? "Failed to load the deal."))
      .finally(() => setLoadingPreview(false));
  };

  // Parse the signed contract (a document on the deal, or an upload): its fee /
  // VAT / APA / delivery and instalments replace the seeded figures — ops' flow.
  const parseContract = async (fileId?: string, file?: File) => {
    if (!preview) return;
    setParsing(true); setParseError(null);
    try {
      const form = new FormData();
      let d: any;
      if (preview.bookingId) {
        form.append("kind", "contract"); if (fileId) form.append("fileId", fileId); if (file) form.append("file", file); form.append("opts", "{}");
        const r: any = await fetch(`/api/bookings/${preview.bookingId}/check/parse`, "POST", form);
        d = r?.data ?? r;
      } else if (file) {
        form.append("file", file);
        const r: any = await fetch(`/api/contracts/parse`, "POST", form);
        d = r?.data ?? r;
      } else { setParseError("Upload the contract — this deal has no booking to read its documents from."); return; }
      const parsed = d?.parsed ?? null;
      const f = parsed?.financials ?? null;
      const name = file?.name ?? preview.files?.find((x) => x.fileId === fileId)?.name ?? "contract";
      setContract({ name, template: d?.template ?? parsed?.template ?? null, note: d?.note ?? d?.error?.message ?? null, parsed });
      setShowPaymentInfo(true);
      if (parsed?.stakeholder?.name) setSupplier({ name: parsed.stakeholder.name ?? "", address: parsed.stakeholder.address ?? "", vatNumber: parsed.stakeholder.vatNumber ?? "" });
      if (parsed?.owner?.name) setOwner({ name: parsed.owner.name ?? "", address: parsed.owner.address ?? "", vatNumber: parsed.owner.vatNumber ?? "" });
      const sb = d?.stakeholderBank;
      if (sb && (sb.iban || sb.accountNumber)) setBank({ currency: sb.currency ?? f?.currency ?? preview.amounts.currency ?? "", beneficiaryName: sb.beneficiaryName ?? parsed?.stakeholder?.name ?? "", bank: sb.bank ?? "", iban: sb.iban ?? "", swift: sb.swift ?? "", accountNumber: sb.accountNumber ?? "", routingNumber: sb.routingNumber ?? "" });
      setCommissionPct((c) => c || "15");
      if (!f || (f.charterFee == null && f.total == null && !(f.paymentSchedule ?? []).length)) { setParseError(d?.error?.message ?? "Nothing usable was read from this document."); return; }
      setComponents((c) => ({
        charterFee: f.charterFee != null ? String(f.charterFee) : c.charterFee,
        vat: f.vat != null ? String(f.vat) : c.vat,
        apa: f.apa != null ? String(f.apa) : c.apa,
        delivery: f.delivery != null ? String(f.delivery) : c.delivery,
      }));
      const rows: SchedRow[] = (f.paymentSchedule ?? []).map((r: any, i: number) => ({ sequence: Number(r.sequence) || i + 1, amount: r.amount != null ? Number(r.amount) : null, dueDate: r.dueDate ?? r.due_date ?? null })).filter((r: SchedRow) => (r.amount ?? 0) > 0 || r.dueDate);
      setContractSchedule(rows.length ? rows : null);
      if (rows.length) { setSchedule(rows); setScheduleTouched(true); }
    } catch (e: any) {
      setParseError(e?.message ?? "Could not parse the contract.");
    } finally { setParsing(false); }
  };

  const setRow = (i: number, patch: Partial<SchedRow>) => { setScheduleTouched(true); setSchedule((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r))); };
  const addRow = () => { setScheduleTouched(true); setSchedule((rows) => [...rows, { sequence: rows.length + 1, amount: null, dueDate: null }]); };
  const removeRow = (i: number) => { setScheduleTouched(true); setSchedule((rows) => rows.filter((_, j) => j !== i).map((r, j) => ({ ...r, sequence: j + 1 }))); };
  const scheduleSum = schedule.reduce((a, r) => a + (Number(r.amount) || 0), 0);

  const numOrNull = (s: string) => (s.trim() === "" ? null : Number(s));
  const total =
    (Number(components.charterFee) || 0) + (Number(components.vat) || 0) + (Number(components.apa) || 0) + (Number(components.delivery) || 0);
  // Card surcharge: on the invoice as its own line AND inside the instalments the
  // client is asked for (deposit → first instalment; full → pro rata), so they add up.
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const ccPctNum = Number(ccPct) > 0 ? Number(ccPct) : 0;
  const ccBase = ccBasis === "deposit" ? Number(schedule[0]?.amount) || total : total;
  const ccFee = ccPctNum > 0 && ccBase > 0 ? r2(ccBase * (ccPctNum / 100)) : 0;
  const ccAddOns = schedule.map((r, i) => (!ccFee ? 0 : ccBasis === "deposit" ? (i === 0 ? ccFee : 0) : r2((Number(r.amount) || 0) * (ccPctNum / 100))));
  if (ccFee && ccAddOns.length) ccAddOns[ccAddOns.length - 1] = r2(ccAddOns[ccAddOns.length - 1] + (ccFee - ccAddOns.reduce((a, b) => a + b, 0)));
  const grossTotal = r2(total + ccFee);
  const grossSum = r2(scheduleSum + ccAddOns.reduce((a, b) => a + b, 0));

  const confirm = () => {
    if (!preview) return;
    setConfirming(true);
    fetch(`/api/finance/payments-inbox/${payment.id}/confirm-booking`, "POST", {
      dealId: preview.deal.hsDealId,
      components: {
        charterFee: numOrNull(components.charterFee),
        vat: numOrNull(components.vat),
        apa: numOrNull(components.apa),
        delivery: numOrNull(components.delivery),
      },
      postPayment: postPayment && !!bankAccountId,
      bankAccountId: bankAccountId || undefined,
      sendReceipt,
      brand: brandChoice ?? undefined,
      schedule: schedule.length ? schedule : undefined,
      scheduleSource: contractSchedule && scheduleTouched ? "contract" : scheduleTouched ? "edited" : preview.scheduleSource ?? "none",
      contractFile: contract?.name ?? undefined,
      contract: contract?.parsed || supplier.name || owner.name ? { template: contract?.template ?? contract?.parsed?.template ?? null, stakeholder: supplier.name ? supplier : null, owner: owner.name ? owner : null, yacht: contract?.parsed?.yacht ?? null } : undefined,
      stakeholderBank: bank.iban || bank.accountNumber ? bank : undefined,
      commissionPct: commissionPct.trim() !== "" && Number.isFinite(Number(commissionPct)) ? Number(commissionPct) : undefined,
      ccSurcharge: Number(ccPct) > 0 ? { pct: Number(ccPct), basis: ccBasis } : undefined,
    })
      .then((r: any) => {
        const d = r?.data ?? {};
        toast({
          title: "Booking confirmed",
          description: `Invoice ${d.xeroInvoiceNumber ?? ""} created on charter account ${d.xeroAccountCode ?? ""}${
            d.paymentPosted?.ok ? " and the payment was posted" : ""
          }${d.scheduleWritten?.written ? `; ${d.scheduleWritten.written} client instalments written` : ""}${d.paymentInfo ? `; supplier${d.paymentInfo.bankOnFile ? " + bank details" : ""} saved${d.paymentInfo.netCharterFee != null ? `, net charter fee ${fmtMoney(d.paymentInfo.netCharterFee, preview.amounts.currency)}` : ""}` : ""}${d.stageAdvanced ? "; HubSpot stage advanced" : d.stageAdvanceError ? "; HubSpot stage advance FAILED — move the deal manually" : ""}${d.receipt?.sent ? (d.receipt.redirected ? "; booking-confirmed receipt sent (test copy to you)" : `; booking-confirmed receipt emailed to ${d.receipt.to}`) : d.receipt?.reason && d.receipt.reason !== "not requested" ? `; receipt NOT sent (${d.receipt.reason})` : ""}.`,
        });
        onDone();
      })
      .catch((e: any) => {
        setError(e?.message ?? "Confirmation failed.");
        setConfirming(false);
      });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !confirming && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirm new booking</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          {fmtMoney(payment.amount, payment.currency ?? "EUR")} · {payment.reference ?? payment.counterparty ?? "no reference"}
        </p>
        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
            {onFindInvoice && /pre-booking stage|already has a booking confirmation|already has a client invoice/i.test(error) && (
              <button type="button" className="ml-2 font-medium underline" onClick={onFindInvoice}>Find the invoice in Xero instead →</button>
            )}
          </div>
        )}

        {!preview ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-700">Which deal is this a deposit for?</p>
            {candidates === null ? (
              <p className="text-sm text-slate-400">Scoring pre-booking deals…</p>
            ) : candidates.length === 0 ? (
              <p className="text-sm text-slate-400">No pre-booking deal matches this payment — enter the HubSpot deal id below.</p>
            ) : (
              <div className="space-y-2">
                {candidates.map((c) => (
                  <button
                    key={c.hsDealId}
                    className="flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left hover:border-blue-400 hover:bg-blue-50/50"
                    onClick={() => loadPreview(c.hsDealId)}
                  >
                    <span>
                      <span className="font-medium">{c.clientName ?? c.dealName ?? c.hsDealId}</span>
                      <span className="block text-xs text-slate-500">
                        {[c.yachtName, c.helmDealId ? `#${c.helmDealId}` : null, c.stageLabel, fmtDate(c.startDate)].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      {c.dealAmount != null && (
                        <span className="text-sm tabular-nums text-slate-600">{fmtMoney(c.dealAmount, c.dealCurrency ?? "EUR")}</span>
                      )}
                      <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", CONF_STYLE[c.confidence])}>{c.signals.join("+")}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <input
                value={manualDealId}
                onChange={(e) => setManualDealId(e.target.value)}
                placeholder="HubSpot deal id or charter code (e.g. 5a044a)…"
                className="w-56 rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
              />
              <Button variant="outline" disabled={!manualDealId.trim() || loadingPreview} onClick={() => loadPreview(manualDealId)}>
                {loadingPreview ? "Loading…" : "Load deal"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <p className="flex flex-wrap items-center gap-x-2 font-medium">
                <span>{preview.deal.dealName ?? preview.deal.hsDealId}</span>
                <span className="font-normal text-slate-500">#{preview.deal.shortHelmDealId} · {preview.deal.stageLabel}</span>
                {preview.alreadyConfirmedStage && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800" title="The deal was moved to the confirmed stage by hand; no charter account or invoice exists yet — confirming raises them">stage moved by hand</span>}
                <BrandBadge brand={preview.brand} />
                {preview.links?.deal && <a className="text-xs font-normal text-blue-600 hover:underline" href={preview.links.deal} target="_blank" rel="noreferrer noopener">Open deal ↗</a>}
                {preview.links?.booking && <a className="text-xs font-normal text-blue-600 hover:underline" href={preview.links.booking} target="_blank" rel="noreferrer noopener">Open booking ↗</a>}
              </p>
              <p className="mt-1 text-slate-600">
                {[preview.deal.yachtName, preview.deal.charterType || "charter type not set", fmtDate(preview.deal.startDate)].filter(Boolean).join(" · ")}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {preview.contact ? `${preview.contact.firstName} ${preview.contact.lastName} · ${preview.contact.email}` : "No contact on the deal"} ·{" "}
                {preview.brand.toUpperCase()} Xero · account “{preview.accountName}” · invoice due {fmtDate(preview.dueDate)}
              </p>
              {preview.brandWarning && <p className="mt-1 text-xs font-medium text-amber-700">⚠ Brand mismatch: {preview.brandWarning}</p>}
              {preview.paymentBrand && preview.pipelineBrand && preview.paymentBrand !== preview.pipelineBrand && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  <span className="text-xs text-slate-600">Raise the invoice in</span>
                  {(["helm", "anchor"] as const).map((o) => (
                    <button key={o} type="button" disabled={loadingPreview} onClick={() => { setBrandChoice(o); loadPreview(preview.deal.hsDealId, o); }} className={cn("rounded px-2.5 py-1 text-xs", preview.brand === o ? "bg-slate-900 text-white" : "border bg-white text-slate-600")}>
                      {o.toUpperCase()} Xero{o === preview.paymentBrand ? " · where the money landed" : o === preview.pipelineBrand ? " · deal's pipeline" : ""}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-lg border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium">Contract</p>
                <label className={cn("cursor-pointer text-xs text-blue-600 hover:underline", parsing && "pointer-events-none opacity-50")}>
                  {parsing ? "Parsing…" : "Upload & parse…"}
                  <input type="file" accept=".pdf,.docx,.txt" className="hidden" disabled={parsing} onChange={(e) => { const f = e.target.files?.[0]; if (f) void parseContract(undefined, f); e.currentTarget.value = ""; }} />
                </label>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">Parse the signed MYBA / CYBA contract to seed the fee, VAT, APA, delivery and instalments — then check them below.</p>
              {preview.files && preview.files.length > 0 ? (
                <div className="mt-2 divide-y">
                  {preview.files.map((f) => (
                    <div key={f.fileId} className="flex items-center justify-between gap-2 py-1 text-xs">
                      <span className="truncate">{f.name}{f.createdAt ? <span className="text-slate-400"> · {fmtDate(f.createdAt)}</span> : null}</span>
                      <button type="button" disabled={parsing} className="shrink-0 text-blue-600 hover:underline disabled:opacity-50" onClick={() => void parseContract(f.fileId)}>Parse</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-400">{preview.filesLive === false ? "Deal documents couldn't be listed — upload the contract." : "No documents on the deal — upload the contract."}</p>
              )}
              {contract && !parseError && <p className="mt-2 text-xs text-green-700">Read {contract.name}{contract.template ? ` · ${contract.template.toUpperCase()}` : ""}{contract.note ? ` · ${contract.note}` : ""} — figures and instalments below come from it.</p>}
              {parseError && <p className="mt-2 text-xs text-red-700">{parseError}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {(["charterFee", "vat", "apa", "delivery"] as const).map((k) => (
                <label key={k} className="text-xs font-medium text-slate-500">
                  {{ charterFee: "Charter fee", vat: "VAT", apa: "APA", delivery: "Delivery" }[k]}
                  <input
                    type="number"
                    value={components[k]}
                    onChange={(e) => setComponents((c) => ({ ...c, [k]: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm font-normal tabular-nums outline-none focus:border-blue-400"
                  />
                </label>
              ))}
            </div>
            <p className="text-sm">
              Invoice total <span className="font-semibold tabular-nums">{fmtMoney(grossTotal, preview.amounts.currency)}</span>
              {ccFee > 0 && <span className="ml-1 text-xs text-slate-500 tabular-nums">= {fmtMoney(total, preview.amounts.currency)} + {fmtMoney(ccFee, preview.amounts.currency)} card surcharge</span>}
              <span className="ml-2 text-xs text-slate-400">
                {preview.deal.category === "crewed" || preview.deal.category === "gulet"
                  ? "billed as Fee / VAT / APA / Delivery lines"
                  : "billed as a single charter line"}{ccFee > 0 ? " + a surcharge line" : ""}
              </span>
            </p>
            <div className="rounded-lg border p-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs font-medium text-slate-500">Credit-card surcharge %
                  <input type="number" step="0.01" min="0" value={ccPct} onChange={(e) => setCcPct(e.target.value)} placeholder="0" className="mt-1 w-24 rounded-md border border-slate-200 px-2 py-1.5 text-sm font-normal tabular-nums outline-none focus:border-blue-400" />
                </label>
                <div className="flex gap-1">
                  {(["deposit", "full"] as const).map((b) => (
                    <button key={b} type="button" disabled={!(ccPctNum > 0)} onClick={() => setCcBasis(b)} className={cn("rounded px-3 py-1 text-xs disabled:opacity-40", ccBasis === b ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600")}>{b === "deposit" ? "on the deposit" : "on the full amount"}</button>
                  ))}
                </div>
                <span className="text-xs text-slate-500">
                  {ccFee > 0
                    ? <>The invoice gets a <b>{fmtMoney(ccFee, preview.amounts.currency)}</b> surcharge line, so it totals <b>{fmtMoney(grossTotal, preview.amounts.currency)}</b>; {ccBasis === "deposit" ? "the 1st instalment" : "each instalment"} written to the deal and the booking includes {ccBasis === "deposit" ? "it" : "its share"}.</>
                    : "0% = no surcharge line. Use it when the client paid by card and the fee is theirs (crewed / gulet card deposits)."}
                </span>
              </div>
            </div>

            <div className="rounded-lg border p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium">Client instalments</p>
                <span className="text-xs text-slate-500">
                  {scheduleTouched ? "edited" : preview.scheduleSource === "hubspot" ? "from HubSpot" : preview.scheduleSource === "booking" ? "from the booking (contract conversion)" : "none on file — add the instalments"}
                  {" · "}written to the deal and the booking on confirm; the invoice is due on the 2nd instalment's date
                </span>
              </div>
              <table className="mt-2 w-full text-xs">
                <thead className="text-left text-[11px] uppercase tracking-wide text-slate-400">
                  <tr><th className="py-1 pr-2">#</th><th className="py-1 pr-2">Contract</th><th className="py-1 pr-2">HubSpot</th><th className="py-1 pr-2">Booking</th><th className="py-1 pr-2">Amount</th><th className="py-1 pr-2">Due</th><th /></tr>
                </thead>
                <tbody>
                  {Array.from({ length: Math.max(schedule.length, contractSchedule?.length ?? 0, preview.hsSchedule?.length ?? 0, preview.bookingSchedule?.length ?? 0) }, (_, i) => {
                    const c = contractSchedule?.[i]; const h = preview.hsSchedule?.[i]; const b = preview.bookingSchedule?.[i]; const e = schedule[i];
                    const cell = (amt: number | null | undefined, due: string | null | undefined) => (amt == null && !due ? <span className="text-slate-300">—</span> : <span className="tabular-nums">{amt != null ? fmtMoney(amt, preview.amounts.currency) : "—"}<span className="block text-[10px] text-slate-400">{due ? fmtDate(due) : "no date"}</span></span>);
                    const differs = c && h && (Math.abs((c.amount ?? 0) - (h.scheduled_amount ?? 0)) > 0.01 || (c.dueDate ?? null) !== (h.scheduled_due_date ?? null));
                    return (
                      <tr key={i} className={cn("border-t align-top", differs && "bg-amber-50/60")}>
                        <td className="py-1.5 pr-2 text-slate-500">{i + 1}</td>
                        <td className="py-1.5 pr-2">{cell(c?.amount, c?.dueDate)}</td>
                        <td className="py-1.5 pr-2">{cell(h?.scheduled_amount, h?.scheduled_due_date)}</td>
                        <td className="py-1.5 pr-2">{cell(b?.amount, b?.dueDate)}{b?.status === "FULLY_PAID" && <span className="block text-[10px] text-green-700">paid</span>}</td>
                        <td className="py-1 pr-2">{e ? <><input type="number" step="0.01" value={e.amount ?? ""} onChange={(ev) => setRow(i, { amount: ev.target.value === "" ? null : Number(ev.target.value) })} className="w-28 rounded-md border border-slate-200 px-2 py-1 tabular-nums outline-none focus:border-blue-400" />{ccAddOns[i] > 0 && <span className="block text-[10px] tabular-nums text-slate-500">+ {fmtMoney(ccAddOns[i], preview.amounts.currency)} surcharge → <b>{fmtMoney(r2((Number(e.amount) || 0) + ccAddOns[i]), preview.amounts.currency)}</b></span>}</> : null}</td>
                        <td className="py-1 pr-2">{e ? <input type="date" value={e.dueDate ?? ""} onChange={(ev) => setRow(i, { dueDate: ev.target.value || null })} className="rounded-md border border-slate-200 px-2 py-1 outline-none focus:border-blue-400" /> : null}</td>
                        <td className="py-1 text-right">{e ? <button type="button" className="text-slate-400 hover:text-red-600" title="Remove instalment" onClick={() => removeRow(i)}>✕</button> : null}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
                <button type="button" className="text-blue-600 hover:underline" onClick={addRow}>+ Add instalment</button>
                {schedule.length > 0 && (
                  <span className={cn("tabular-nums", Math.abs(grossSum - grossTotal) > 0.5 ? "font-medium text-amber-700" : "text-slate-500")}>
                    Instalments {fmtMoney(scheduleSum, preview.amounts.currency)}{ccFee > 0 ? ` + surcharge ${fmtMoney(ccFee, preview.amounts.currency)} = ${fmtMoney(grossSum, preview.amounts.currency)}` : ""}{Math.abs(grossSum - grossTotal) > 0.5 ? ` ≠ invoice ${fmtMoney(grossTotal, preview.amounts.currency)} (${grossSum > grossTotal ? "+" : "−"}${fmtMoney(Math.abs(grossSum - grossTotal), preview.amounts.currency)})` : " = invoice total"}
                  </span>
                )}
              </div>
            </div>

            <div className="rounded-lg border p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-medium">Supplier & payment information</p>
                <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setShowPaymentInfo((v) => !v)}>{showPaymentInfo ? "Hide" : "Add"}</button>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">Who we pay and where, and HELM's cut — saved on the booking (bank details feed Pay via Revolut) and the net fee to HubSpot. Seeded from the parsed contract.</p>
              {showPaymentInfo && (() => {
                const fee = Number(components.charterFee) || 0;
                const pct = Number(commissionPct);
                const net = fee && Number.isFinite(pct) ? Math.round(fee * (1 - pct / 100) * 100) / 100 : null;
                const field = (label: string, value: string, set: (v: string) => void, extra?: string) => (
                  <label className="text-xs font-medium text-slate-500">{label}<input value={value} onChange={(e) => set(e.target.value)} className={cn("mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm font-normal outline-none focus:border-blue-400", extra)} /></label>
                );
                return (
                  <div className="mt-2 space-y-3">
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {field("Stakeholder (we pay)", supplier.name, (v) => setSupplier((x) => ({ ...x, name: v })))}
                      {field("Stakeholder address", supplier.address, (v) => setSupplier((x) => ({ ...x, address: v })))}
                      {field("Owner (commission invoice)", owner.name, (v) => setOwner((x) => ({ ...x, name: v })))}
                      <label className="text-xs font-medium text-slate-500">HELM commission %
                        <input type="number" step="0.01" value={commissionPct} onChange={(e) => setCommissionPct(e.target.value)} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm font-normal tabular-nums outline-none focus:border-blue-400" />
                        <span className="mt-0.5 block text-[11px] font-normal text-slate-500">{net != null ? `net charter fee ${fmtMoney(net, preview.amounts.currency)}` : "net charter fee —"}</span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {field("Account currency", bank.currency, (v) => setBank((x) => ({ ...x, currency: v.toUpperCase() })))}
                      {field("Beneficiary name", bank.beneficiaryName, (v) => setBank((x) => ({ ...x, beneficiaryName: v })))}
                      {field("Bank", bank.bank, (v) => setBank((x) => ({ ...x, bank: v })))}
                      {field("IBAN", bank.iban, (v) => setBank((x) => ({ ...x, iban: v })), "font-mono")}
                      {field("SWIFT / BIC", bank.swift, (v) => setBank((x) => ({ ...x, swift: v })), "font-mono")}
                      {field("Account number", bank.accountNumber, (v) => setBank((x) => ({ ...x, accountNumber: v })), "font-mono")}
                      {field("ABA / routing (USD)", bank.routingNumber, (v) => setBank((x) => ({ ...x, routingNumber: v })), "font-mono")}
                    </div>
                    <p className="text-[11px] text-slate-500">{bank.iban || bank.accountNumber ? "✓ Bank details will be on file — the supplier can be paid via Revolut from the payables screen." : "No bank details yet — the supplier can't be paid via Revolut until they're added (here, or later on the booking)."}</p>
                  </div>
                );
              })()}
            </div>

            <label className="flex items-start gap-2 rounded-lg border p-3 text-sm">
              <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} className="mt-1" />
              <span>Email the booking-confirmed receipt to {preview.contact?.email ? <b>{preview.contact.email}</b> : "the client"} once the payment is on the booking<span className="block text-xs text-slate-500">Charter, charges, this payment and the instalments still to come — the same receipt the Payments tab sends, logged on the payment. Untick if the client was already receipted.</span></span>
            </label>
            <div className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={postPayment} onChange={(e) => setPostPayment(e.target.checked)} />
                Post this {fmtMoney(payment.amount, payment.currency ?? "EUR")} payment to
              </label>
              <select
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                disabled={!postPayment}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-blue-400 disabled:opacity-50"
              >
                <option value="">Choose bank account…</option>
                {preview.bankAccounts.candidates.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.name}{a.currency ? ` (${a.currency})` : ""}
                  </option>
                ))}
              </select>
              {(preview.bankAccounts as any).how && bankAccountId === preview.bankAccounts.default?.accountId && (
                <span className="basis-full text-xs text-slate-500">
                  {({ bank_feed: "This bank line already sits in this account.", card_rule: "House rule: Visa / Mastercard → the Nuvei (Anchor) or Ryft (HELM) account in this currency; Amex → the Revolut Main account.", routing_rule: "Per the ops routing rule for this brand, network and currency.", currency_default: "The default account for this currency.", heuristic: "The only account for this currency named after the source." } as Record<string, string>)[(preview.bankAccounts as any).how]}
                </span>
              )}
            </div>
            <button className="text-xs text-blue-600 hover:underline" onClick={() => { setPreview(null); setError(null); }}>
              ← choose a different deal
            </button>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={confirming} onClick={onClose}>
            Cancel
          </Button>
          {preview && (
            <Button disabled={confirming || total <= 0 || (postPayment && !bankAccountId)} onClick={confirm}>
              {confirming ? "Confirming…" : "Confirm booking"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------- Matched tab ----------------------------- */

function MatchedTab({ matched, onChanged }: { matched: MatchedPayment[] | null; onChanged: () => void }) {
  const { fetch } = useFetch();
  const readOnly = useReadOnly();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<"all" | "unallocated" | "allocated">("unallocated");
  const [allocating, setAllocating] = useState<MatchedPayment | null>(null);
  const [findInvoiceFor, setFindInvoiceFor] = useState<MatchedPayment | null>(null);
  const [confirmFor, setConfirmFor] = useState<MatchedPayment | null>(null);
  const [rematching, setRematching] = useState<MatchedPayment | null>(null);
  const [notesFor, setNotesFor] = useState<MatchedPayment | null>(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const reconcile = (m: MatchedPayment) => {
    setBusy((b) => ({ ...b, [m.id]: true }));
    fetch<{ data: { found: boolean; already?: boolean; invoiceNumber?: string | null; date?: string | null; checked?: string[] } }>(`/api/finance/payments-inbox/${m.id}/reconcile`, "POST")
      .then((r) => {
        const d = r.data;
        toast(d.found
          ? { title: "Found in Xero", description: `${fmtMoney(m.amount, m.currency ?? "EUR")} is already on ${d.invoiceNumber ? `invoice ${d.invoiceNumber}` : "the invoice"}${d.date ? ` (${fmtDate(d.date)})` : ""} — recorded as allocated, nothing posted.` }
          : { title: "Not on any invoice", description: `No Xero payment of ${fmtMoney(m.amount, m.currency ?? "EUR")} on ${d.checked?.length ? d.checked.join(", ") : "the booking's invoices"} — allocate it from here if it really is unposted.`, variant: "destructive" });
        onChanged();
      })
      .catch((e: any) => toast({ title: "Check failed", description: e?.message ?? "", variant: "destructive" }))
      .finally(() => setBusy((b) => ({ ...b, [m.id]: false })));
  };

  const writeOffResidual = (m: MatchedPayment) => {
    if (!m.residual) return;
    if (!window.confirm(`Write off ${fmtMoney(m.residual.amountDue, m.residual.currency ?? m.currency ?? "EUR")} on invoice ${m.residual.invoiceNumber ?? ""} to Bank Fees? A credit note is raised in Xero and allocated to the invoice, closing it.`)) return;
    setBusy((b) => ({ ...b, [m.id]: true }));
    fetch<{ data: { amount: number; id: string | null; invoiceNumber: string | null; currency: string | null } }>(`/api/finance/payments-inbox/${m.id}/write-off-residual`, "POST", { invoiceId: m.residual.invoiceId })
      .then((r) => { toast({ title: "Written off to bank fees", description: `${fmtMoney(r.data.amount, r.data.currency ?? m.currency ?? "EUR")} credit note on ${r.data.invoiceNumber ?? "the invoice"} — it is now paid in full.` }); onChanged(); })
      .catch((e: any) => toast({ title: "Write-off failed", description: e?.message ?? "", variant: "destructive" }))
      .finally(() => setBusy((b) => ({ ...b, [m.id]: false })));
  };
  const unallocate = (m: MatchedPayment) => {
    if (!window.confirm(`Void the ${fmtMoney(m.amount, m.currency ?? "EUR")} Xero payment for ${m.clientName ?? "this booking"}? The invoice's amount due goes back up.`)) return;
    setBusy((b) => ({ ...b, [m.id]: true }));
    fetch(`/api/finance/payments-inbox/${m.id}/unallocate`, "POST")
      .then(() => {
        toast({ title: "Payment voided", description: "The Xero payment was deleted; the match is kept." });
        onChanged();
      })
      .catch((e: any) => toast({ title: "Error", description: e?.message ?? "Failed to void the payment.", variant: "destructive" }))
      .finally(() => setBusy((b) => ({ ...b, [m.id]: false })));
  };

  const visible = useMemo(
    () =>
      (matched ?? []).filter(
        (m) =>
          (filter === "all" ? true : filter === "allocated" ? m.allocated : !m.allocated) &&
          (!search ||
            [m.reference, m.counterparty, m.clientName, m.yachtName, m.accountCode]
              .filter(Boolean)
              .some((v) => String(v).toLowerCase().includes(search.toLowerCase()))),
      ),
    [matched, filter, search],
  );

  const unmatch = (m: MatchedPayment) => {
    setBusy((b) => ({ ...b, [m.id]: true }));
    fetch(`/api/finance/payments-inbox/${m.id}/unmatch`, "POST")
      .then(() => {
        toast({ title: "Unmatched", description: "The payment is back in the To match tab." });
        onChanged();
      })
      .catch((e: any) => toast({ title: "Error", description: e?.message ?? "Failed to unmatch.", variant: "destructive" }))
      .finally(() => setBusy((b) => ({ ...b, [m.id]: false })));
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {(
          [
            ["unallocated", "Needs allocation"],
            ["allocated", "Allocated"],
            ["all", "All"],
          ] as ["all" | "unallocated" | "allocated", string][]
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm",
              filter === k ? "border-slate-800 bg-slate-800 text-white" : "bg-white hover:bg-slate-50",
            )}
          >
            {label}
          </button>
        ))}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search client, yacht, code, reference…"
          className="ml-auto w-72 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400"
        />
      </div>

      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Received</th>
              <th className="px-3 py-2">Matched to</th>
              <th className="px-3 py-2">Match</th>
              <th className="px-3 py-2">Allocation</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {matched === null ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Nothing here.</td></tr>
            ) : (
              visible.map((m) => (
                <tr key={m.id} className={cn("border-t align-top", !m.allocated && "border-l-2 border-l-amber-400")}>
                  <td className="whitespace-nowrap px-3 py-2">
                    <span className="font-medium tabular-nums">{fmtMoney(m.amount, m.currency ?? "EUR")}</span>
                    <span className="block text-xs text-slate-400">
                      <SourcePill source={m.source} bankAccountName={m.bankAccountName} className="mr-1.5" />{fmtDate(m.occurredAt)}
                      <button className={cn("ml-2 hover:text-slate-700 hover:underline", m.noteCount && "font-medium text-slate-600")} onClick={() => setNotesFor(m)}>
                        {m.noteCount ? `${m.noteCount} note${m.noteCount > 1 ? "s" : ""}` : "+ note"}
                      </button>
                    </span>
                    <PaymentMeta status={m.status} cardLabel={m.cardLabel} card={m.card} />
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{m.clientName ?? "—"}</span>
                    <span className="block text-xs text-slate-500">
                      {[m.yachtName, m.accountCode ? `#${m.accountCode}` : null, m.brand].filter(Boolean).join(" · ")}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    <span className={cn("rounded-full px-2 py-0.5 font-medium", m.matchMethod === "manual" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-600")}>
                      {m.matchMethod === "nuvei_booking_id" ? "booking page (card)" : m.matchMethod ?? "—"}
                    </span>
                    <span className="mt-1 block">{m.matchedBy ?? ""}{m.matchedAt ? ` · ${fmtDate(m.matchedAt)}` : ""}</span>
                  </td>
                  <td className="px-3 py-2">
                    {m.allocated ? (
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                        Allocated{m.xeroPaymentAt ? ` · ${fmtDate(m.xeroPaymentAt)}` : ""}
                      </span>
                    ) : (
                      <>
                        {m.opsSaysInXero && (
                          <span className="mb-1 inline-flex flex-wrap items-center gap-1.5">
                            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800" title="The ops sync marked this payment 'already in Xero' — it was posted by ops' flow or keyed into Xero directly. Check Xero finds that payment on the booking's invoice and records it here; nothing is posted.">Ops: already in Xero</span>
                            <button type="button" disabled={!!busy[m.id]} className="text-[11px] text-blue-600 hover:underline" onClick={() => reconcile(m)}>{busy[m.id] ? "Checking…" : "Check Xero…"}</button>
                          </span>
                        )}
                        {m.noInvoice && m.canConfirm ? (
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setConfirmFor(m)}
                              className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-800 hover:bg-violet-200"
                              title="The booking has no invoice and its deal was never confirmed — confirm it to raise the charter account and invoice (parse the contract, set the instalments) and post this payment"
                            >
                              ⚡ No invoice yet · confirm booking…
                            </button>
                            <button type="button" onClick={() => setFindInvoiceFor(m)} className="text-[11px] text-slate-500 hover:underline" title="The invoice already exists in Xero — find and link it">find in Xero</button>
                          </span>
                        ) : m.noInvoice ? (
                          <button
                            type="button"
                            onClick={() => setFindInvoiceFor(m)}
                            className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 hover:bg-red-200"
                            title="No Xero invoice is linked to this booking, so there is nothing to allocate against — search Xero and link it"
                          >
                            No invoice linked · find in Xero…
                          </button>
                        ) : (
                          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">Not in Xero yet</span>
                        )}
                        {m.xeroPaymentError && (
                          <span className="mt-1 block max-w-[240px] text-xs text-red-600">{m.xeroPaymentError}</span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {readOnly ? (
                      <button className="text-sm text-blue-600 hover:underline" onClick={() => navigate(`/bookings/${m.bookingId}?tab=payments`)}>Open</button>
                    ) : (
                    <span className="flex items-center gap-2">
                      {!m.allocated && (
                        <button
                          className="rounded-md bg-blue-600 px-2.5 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                          disabled={!!busy[m.id]}
                          onClick={() => setAllocating(m)}
                        >
                          Allocate
                        </button>
                      )}
                      <button className="text-sm text-blue-600 hover:underline" onClick={() => navigate(`/bookings/${m.bookingId}?tab=payments`)}>
                        Open
                      </button>
                      {m.canChangeMatch && (
                        <button className="text-sm text-slate-500 hover:underline disabled:opacity-50" disabled={!!busy[m.id]} onClick={() => setRematching(m)}>
                          Change match
                        </button>
                      )}
                      {m.canUnmatch && (
                        <button className="text-sm text-slate-500 hover:underline disabled:opacity-50" disabled={!!busy[m.id]} onClick={() => unmatch(m)}>
                          Unmatch
                        </button>
                      )}
                      {m.canUnallocate && (
                        <button className="text-sm text-amber-700 hover:underline disabled:opacity-50" disabled={!!busy[m.id]} onClick={() => unallocate(m)}>
                          Unallocate
                        </button>
                      )}
                      {m.allocated && m.residual && (
                        <button className="text-sm text-amber-700 hover:underline disabled:opacity-50" disabled={!!busy[m.id]} title={`Invoice ${m.residual.invoiceNumber ?? ""} still shows ${fmtMoney(m.residual.amountDue, m.residual.currency ?? m.currency ?? "EUR")} outstanding — close it with a credit note coded to Bank Fees (404), as ops did for short payments.`} onClick={() => writeOffResidual(m)}>
                          Write off {fmtMoney(m.residual.amountDue, m.residual.currency ?? m.currency ?? "EUR")} to bank fees
                        </button>
                      )}
                    </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        Allocate posts the Xero Payment against the booking's invoice, coded to the bank account you pick. Unallocated
        matches can be changed or unmatched — your decision sticks (the ops sync no longer overrides it). Unallocate
        voids a payment posted from here; payments posted by the ops flow are undone there.
      </p>

      {allocating && (
        <AllocateDialog
          payment={allocating}
          onClose={() => setAllocating(null)}
          onDone={() => {
            setAllocating(null);
            onChanged();
          }}
        />
      )}
      {confirmFor && (
        <ConfirmBookingDialog
          payment={confirmFor}
          initialDealId={confirmFor.hsDealId ?? null}
          onClose={() => setConfirmFor(null)}
          onFindInvoice={() => { const m = confirmFor; setConfirmFor(null); setFindInvoiceFor(m); }}
          onDone={() => { setConfirmFor(null); onChanged(); }}
        />
      )}
      {findInvoiceFor && (
        <XeroInvoiceSearchDialog
          bookingId={findInvoiceFor.bookingId}
          bookingLabel={findInvoiceFor.clientName}
          brand={findInvoiceFor.brand}
          initialQuery={findInvoiceFor.clientName?.split(/\s+/).pop() ?? findInvoiceFor.counterparty}
          onClose={() => setFindInvoiceFor(null)}
          onLinked={() => {
            // Straight on to allocating against the invoice just linked.
            const m = findInvoiceFor;
            setFindInvoiceFor(null);
            onChanged();
            setAllocating({ ...m, noInvoice: false });
          }}
        />
      )}
      {notesFor && (
        <NotesDialog
          paymentId={notesFor.id}
          label={`${fmtMoney(notesFor.amount, notesFor.currency ?? "EUR")} · ${notesFor.clientName ?? notesFor.reference ?? ""}`}
          onClose={() => setNotesFor(null)}
          onChanged={onChanged}
        />
      )}
      {rematching && (
        <BookingSearchDialog
          title="Change match"
          subtitle={`${fmtMoney(rematching.amount, rematching.currency ?? "EUR")} · currently ${rematching.clientName ?? "unknown"}${rematching.accountCode ? ` (#${rematching.accountCode})` : ""}`}
          endpoint={`/api/finance/payments-inbox/${rematching.id}/rematch`}
          suggestions={quickSearchTerms(rematching.reference, rematching.counterparty)}
          paymentCurrency={rematching.currency}
          paymentAmount={rematching.amount}
          onClose={() => setRematching(null)}
          onDone={(result, bookingId, hit) => {
            const m = rematching;
            setRematching(null);
            const x = result?.xero;
            if (x && !x.allocated && bookingId) {
              const moved: MatchedPayment = { ...m, bookingId, clientName: hit?.clientName ?? m.clientName, yachtName: hit?.yachtName ?? m.yachtName, brand: hit?.brand ?? m.brand, accountCode: hit?.accountCode ?? m.accountCode, allocated: false, noInvoice: !!x.needsInvoice };
              if (x.needsInvoice && x.canConfirm && x.hsDealId) setConfirmFor({ ...moved, canConfirm: true, hsDealId: x.hsDealId });
              else if (x.needsInvoice) setFindInvoiceFor(moved); else setAllocating(moved);
            }
            onChanged();
          }}
        />
      )}
    </>
  );
}

function AllocateDialog({ payment, onClose, onDone }: { payment: MatchedPayment; onClose: () => void; onDone: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [options, setOptions] = useState<AllocationOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invoiceId, setInvoiceId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [posting, setPosting] = useState(false);
  // Extras / APA route (ops parity): the payment exceeds what the open invoice
  // needs, or there is no open invoice, because it covers extras or APA agreed
  // after invoicing. Raises the extras invoice (+ optional bill) and posts both payments.
  const [mode, setMode] = useState<"invoice" | "extras">("invoice");
  const [description, setDescription] = useState("");
  const [recipient, setRecipient] = useState<"none" | "supplier" | "client">("none");
  const [finding, setFinding] = useState(false);
  // Ops brand rule: which org to post into (the invoice's by default; switchable on a mismatch).
  const [org, setOrg] = useState<"helm" | "anchor" | null>(null);
  // Ops currency rule: the amount to post in the invoice's currency when the payment is in another.
  const [fxAmount, setFxAmount] = useState("");
  const [fxConfirmed, setFxConfirmed] = useState(false);
  // Ops' trivial-delta write-offs for a same-currency short / over payment.
  const [writeOff, setWriteOff] = useState<"" | "underpayment_bank_fees" | "overpayment_cogs" | "overpayment_card_fee">("");
  const [writeOffDescription, setWriteOffDescription] = useState("");

  const loadOptions = (o: "helm" | "anchor" | null = org) => {
    setOptions(null);
    fetch<{ data: AllocationOptions }>(`/api/finance/payments-inbox/${payment.id}/allocation-options${o ? `?brand=${o}` : ""}`)
      .then((r) => {
        setOptions(r.data);
        const inv = r.data.invoices.find((i) => Math.abs(i.amountDue - payment.amount) < 0.01) ?? r.data.invoices[0];
        if (inv) setInvoiceId(inv.invoiceId);
        if (r.data.suggestedAccount) setAccountId(r.data.suggestedAccount.accountId);
        else if (r.data.bankAccounts[0]) setAccountId(r.data.bankAccounts[0].accountId);
        // Nothing covers the amount → this is extras / APA money; an invoice
        // that does cover it → pay it (also after one was just linked). A
        // cross-currency invoice is paid in its own currency, never as extras.
        const cross = inv?.crossCurrency ?? null;
        setFxAmount(cross ? String(cross.suggestedAmount) : "");
        setMode(cross || r.data.invoices.some((i) => i.amountDue >= payment.amount - 0.01) ? "invoice" : "extras");
      })
      .catch((e: any) => setError(e?.message ?? "Failed to load allocation options."));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => loadOptions(null), [payment.id]);

  const chosen = options?.invoices.find((i) => i.invoiceId === invoiceId) ?? null;
  const cross = mode === "invoice" && chosen?.crossCurrency ? chosen.crossCurrency : null;
  const fxValue = Number(fxAmount) || 0;
  const bothOrgs = !!options?.orgs && options.orgs.helm > 0 && options.orgs.anchor > 0;
  const due = mode === "extras" && chosen ? Math.min(payment.amount, Math.max(0, chosen.amountDue)) : 0;
  const excess = mode === "extras" ? Math.round((payment.amount - due) * 100) / 100 : 0;
  // Same-currency difference between the money and the invoice balance (ops' write-off panel).
  const ccy = payment.currency ?? "EUR";
  const delta = mode === "invoice" && chosen && !cross ? Math.round((payment.amount - chosen.amountDue) * 100) / 100 : 0;
  const short = delta < -0.01 ? -delta : 0;
  const over = delta > 0.01 ? delta : 0;
  const UNDERPAY_MAX = 200, COGS_MAX = 100;
  // Cross-currency: the rate the two amounts imply vs spot — more than 5% off needs a tick (ops blocked outright).
  // spotRate from the API is invoice-currency per 1 payment-currency; the implied rate is the other way round.
  const implied = cross && fxValue > 0 ? payment.amount / fxValue : null;
  const spotPerInvoice = cross?.spotRate ? 1 / cross.spotRate : null;
  const fxDeltaPct = implied != null && spotPerInvoice ? ((implied - spotPerInvoice) / spotPerInvoice) * 100 : null;
  const fxOff = fxDeltaPct != null && Math.abs(fxDeltaPct) > 5;
  const post = () => {
    setPosting(true);
    const req = mode === "extras"
      ? fetch(`/api/finance/payments-inbox/${payment.id}/extras-apa`, "POST", { originalInvoiceId: invoiceId || null, description, billRecipient: recipient, accountId })
      : fetch(`/api/finance/payments-inbox/${payment.id}/allocate`, "POST", { invoiceId, accountId, amount: cross ? fxValue : undefined, writeOff: writeOff || undefined, writeOffDescription: writeOff === "overpayment_card_fee" ? writeOffDescription || undefined : undefined, fxConfirmed: cross ? fxConfirmed : undefined });
    req
      .then((r: any) => {
        const d = r?.data;
        const wo = d?.writeOff;
        const woText = !wo ? "" : wo.ok
          ? wo.mode === "underpayment_bank_fees" ? ` ${fmtMoney(wo.amount, ccy)} short written off to Bank Fees (credit note) — invoice paid in full.`
            : wo.mode === "overpayment_cogs" ? ` ${fmtMoney(wo.amount, ccy)} over booked to COGS.`
            : ` ${fmtMoney(wo.amount, ccy)} card fee invoiced${wo.invoiceNumber ? ` (${wo.invoiceNumber})` : ""} to Bank Fees and paid.`
          : ` Payment posted but the write-off step failed: ${wo.error}`;
        toast({
          title: d?.deduped ? "Already in Xero — recorded, not posted again" : mode === "extras" ? "Extras / APA invoiced and paid" : wo && !wo.ok ? "Allocated — write-off step failed" : "Allocated",
          description: mode === "extras" && d?.invoice ? `${fmtMoney(d.excess, d.currency)} on new invoice ${d.invoice.number ?? ""}${d.due ? ` · ${fmtMoney(d.due, d.currency)} on the original` : ""}${(d.written ?? []).some((w: any) => !w.ok) ? " — one step failed, see the row's Xero note" : ""}` : `Xero payment posted against the invoice.${d?.viaCharterAccount ? ` Routed through charter account ${d.viaCharterAccount.accountCode} (three currencies).` : ""}${woText}${d?.dueDateMovedTo ? ` Due date moved to ${fmtDate(d.dueDateMovedTo)} (next instalment).` : ""}`,
          ...(wo && !wo.ok ? { variant: "destructive" as const } : {}),
        });
        onDone();
      })
      .catch((e: any) => {
        toast({ title: "Xero rejected the payment", description: e?.message ?? "Allocation failed.", variant: "destructive" });
        setPosting(false);
      });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Allocate {fmtMoney(payment.amount, payment.currency ?? "EUR")} · {payment.clientName ?? ""}
          </DialogTitle>
        </DialogHeader>
        {error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : !options ? (
          <p className="text-sm text-slate-500">Loading invoices and bank accounts…</p>
        ) : (
          <div className="flex flex-col gap-4 text-sm">
            {(options.brandMismatch || bothOrgs) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                {options.brandMismatch && (
                  <p className="text-xs font-medium text-amber-800">
                    ⚠ Brand mismatch: the money landed in {options.brandMismatch.payment.toUpperCase()}'s accounts, the invoice is in {options.brandMismatch.target.toUpperCase()}'s Xero. A payment can only be posted in the org that holds the invoice — post it there (into that org's account for this source), or switch org if the booking is invoiced there too.
                  </p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  <span className="text-xs text-slate-600">Post into</span>
                  {(["helm", "anchor"] as const).map((o) => (
                    <button key={o} type="button" onClick={() => { setOrg(o); loadOptions(o); }} className={cn("rounded px-2.5 py-1 text-xs", options.brand === o ? "bg-slate-900 text-white" : "bg-white text-slate-600 border")}>
                      {o.toUpperCase()} Xero · {options.orgs?.[o] ?? 0} open invoice{(options.orgs?.[o] ?? 0) === 1 ? "" : "s"}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-1">
              {(["invoice", "extras"] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} className={cn("rounded px-3 py-1 text-xs", mode === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600")}>
                  {m === "invoice" ? "Pay an invoice" : "Extras / APA"}
                </button>
              ))}
            </div>
            <label className="flex flex-col gap-1">
              <span className="font-medium">{mode === "extras" ? "Original invoice (optional — its balance is paid first)" : "Against invoice"}</span>
              {options.invoices.length === 0 ? (
                <span className="flex flex-wrap items-center gap-2 text-slate-500">
                  <span>{mode === "extras" ? "No open invoice — the whole amount goes on a new extras / APA invoice." : "No open client invoice is linked to this booking."}</span>
                  <button type="button" onClick={() => setFinding(true)} className="rounded-md border px-2 py-0.5 text-xs font-medium text-slate-700 hover:border-blue-400 hover:text-blue-700">
                    Find invoice in Xero…
                  </button>
                </span>
              ) : (
                <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} className="rounded-md border border-slate-200 bg-white px-3 py-2">
                  {mode === "extras" && <option value="">— none, all of it is extras / APA —</option>}
                  {options.invoices.map((i) => (
                    <option key={i.invoiceId} value={i.invoiceId}>
                      {i.invoiceNumber ?? i.invoiceId.slice(0, 8)} · due {fmtMoney(i.amountDue, i.currency ?? "EUR")}
                      {i.dueDate ? ` · ${fmtDate(i.dueDate)}` : ""}{i.category ? ` · ${i.category}` : ""}
                    </option>
                  ))}
                </select>
              )}
            </label>
            {cross && (
              <label className="flex flex-col gap-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <span className="font-medium text-amber-900">⚠ Currency mismatch — amount to post in {cross.invoiceCurrency}</span>
                <span className="text-xs text-amber-800">
                  Received {fmtMoney(payment.amount, payment.currency ?? "EUR")}; the invoice is in {cross.invoiceCurrency}.
                  {cross.noRate ? " No rate on file — defaulting to the full balance." : ` At today's rate that is ${fmtMoney(cross.converted ?? 0, cross.invoiceCurrency)}${cross.closes ? ", within 5% of the balance, so the invoice is closed in full" : ""}.`}
                  {" "}Xero converts to the bank account at its own rate and books the FX difference.
                </span>
                <input type="number" step="0.01" min="0" max={chosen?.amountDue ?? undefined} value={fxAmount} onChange={(e) => setFxAmount(e.target.value)} className="w-48 rounded-md border border-amber-300 bg-white px-3 py-1.5 tabular-nums" />
                {fxValue > 0 && <span className="text-xs text-amber-800">Implied rate 1 {cross.invoiceCurrency} = {(payment.amount / fxValue).toFixed(4)} {payment.currency}{spotPerInvoice ? ` · spot ${spotPerInvoice.toFixed(4)}${fxDeltaPct != null ? ` (${fxDeltaPct > 0 ? "+" : ""}${fxDeltaPct.toFixed(1)}%)` : ""}` : ""}</span>}
                {fxOff && (
                  <label className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-800">
                    <input type="checkbox" checked={fxConfirmed} onChange={(e) => setFxConfirmed(e.target.checked)} className="mt-0.5" />
                    <span>The implied rate is more than 5% off today's spot — usually a wrong invoice or a typo. I've checked the amounts; post anyway.</span>
                  </label>
                )}
                {chosen && options.bankAccounts.find((a) => a.accountId === accountId)?.currency && ![cross.invoiceCurrency, "GBP"].includes(String(options.bankAccounts.find((a) => a.accountId === accountId)?.currency).toUpperCase()) && (
                  <span className="text-xs text-amber-800">Three currencies (bank account, invoice, GBP books): the receipt is routed through the booking's charter account, the way ops did it.</span>
                )}
              </label>
            )}
            {mode === "invoice" && chosen && (short > 0 || over > 0) && (
              <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <span className="font-medium text-amber-900">
                  {short > 0 ? `Short by ${fmtMoney(short, ccy)}` : `${fmtMoney(over, ccy)} more than the invoice needs`} — {chosen.invoiceNumber ?? "the invoice"} has {fmtMoney(chosen.amountDue, ccy)} outstanding
                </span>
                <div className="flex flex-col gap-1 text-xs text-amber-900">
                  {short > 0 && (
                    <>
                      <label className="flex items-center gap-2"><input type="radio" name="wo" checked={writeOff === ""} onChange={() => setWriteOff("")} />Post {fmtMoney(payment.amount, ccy)} and leave {fmtMoney(short, ccy)} outstanding (chase it, or write it off later from the Matched tab)</label>
                      <label className={cn("flex items-center gap-2", short > UNDERPAY_MAX && "opacity-50")}><input type="radio" name="wo" disabled={short > UNDERPAY_MAX} checked={writeOff === "underpayment_bank_fees"} onChange={() => setWriteOff("underpayment_bank_fees")} />Write {fmtMoney(short, ccy)} off to Bank Fees — a credit note closes the invoice (bank charges on the way in){short > UNDERPAY_MAX ? ` · over the ${UNDERPAY_MAX} cap` : ""}</label>
                    </>
                  )}
                  {over > 0 && (
                    <>
                      <label className="flex items-center gap-2"><input type="radio" name="wo" checked={writeOff === "overpayment_card_fee"} onChange={() => setWriteOff("overpayment_card_fee")} />Card fee: invoice the {fmtMoney(over, ccy)} to the client coded to Bank Fees and pay it from the same account (the surcharge they paid by card)</label>
                      <label className={cn("flex items-center gap-2", over > COGS_MAX && "opacity-50")}><input type="radio" name="wo" disabled={over > COGS_MAX} checked={writeOff === "overpayment_cogs"} onChange={() => setWriteOff("overpayment_cogs")} />Small overpayment: take {fmtMoney(over, ccy)} as a gain against COGS{over > COGS_MAX ? ` · over the ${COGS_MAX} cap` : ""}</label>
                      <label className="flex items-center gap-2"><input type="radio" name="wo" checked={writeOff === ""} onChange={() => { setWriteOff(""); setMode("extras"); }} />Extras / APA: the excess is for extras agreed after invoicing</label>
                      {writeOff === "overpayment_card_fee" && (
                        <input value={writeOffDescription} onChange={(e) => setWriteOffDescription(e.target.value)} placeholder={`Card processing fee · overpayment on ${chosen.invoiceNumber ?? "the invoice"}`} className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs text-slate-800" />
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            <label className="flex flex-col gap-1">
              <span className="font-medium">Into bank account ({options.brand})</span>
              {options.brandNote && <span className="rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">{options.brandNote}</span>}
              {options.suggestedAccount && accountId === options.suggestedAccount.accountId && (
                <span className="text-xs text-slate-500">
                  {({ bank_feed: "This bank line already sits in this account.", card_rule: "House rule: Visa / Mastercard → the Nuvei (Anchor) or Ryft (HELM) account in this currency; Amex → the Revolut Main account.", routing_rule: "Per the ops routing rule for this brand, network and currency.", currency_default: "The default account for this currency.", heuristic: "The only account for this currency named after the source." } as Record<string, string>)[options.suggestedAccount.how]}
                </span>
              )}
              {options.suggestedAccount && accountId !== options.suggestedAccount.accountId && (
                <span className="text-xs text-amber-700">Routing would use “{options.suggestedAccount.name}” — you've picked a different account.</span>
              )}
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className="rounded-md border border-slate-200 bg-white px-3 py-2">
                {options.bankAccounts.map((a) => (
                  <option key={a.accountId} value={a.accountId}>
                    {a.name}{a.code ? ` (${a.code})` : ""}{a.currency ? ` · ${a.currency}` : ""}
                  </option>
                ))}
              </select>
            </label>
            {mode === "extras" && (
              <>
                <label className="flex flex-col gap-1">
                  <span className="font-medium">Line description</span>
                  <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={chosen ? `Extras / APA (against ${chosen.invoiceNumber ?? "invoice"})` : "Extras / APA"} className="rounded-md border border-slate-200 bg-white px-3 py-2" />
                </label>
                <div className="flex flex-col gap-1">
                  <span className="font-medium">Also raise a bill for the extras</span>
                  <div className="flex flex-wrap gap-1">
                    {([["none", "No bill"], ["supplier", "To the supplier (passthrough)"], ["client", "To the client (we owe it back)"]] as const).map(([k, l]) => (
                      <button key={k} type="button" onClick={() => setRecipient(k)} className={cn("rounded px-3 py-1 text-xs", recipient === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600")}>{l}</button>
                    ))}
                  </div>
                </div>
                <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-600">
                  {due > 0 && <>{fmtMoney(due, payment.currency ?? "EUR")} pays {chosen?.invoiceNumber ?? "the original"} · </>}
                  <b>{fmtMoney(excess, payment.currency ?? "EUR")}</b> goes on a new extras / APA invoice to the client, paid at once{recipient !== "none" ? `, with a matching bill to the ${recipient}` : ""}.
                </p>
              </>
            )}
            <p className="text-xs text-slate-400">
              Posts a Xero Payment dated {fmtDate(payment.occurredAt)} with reference “{payment.reference ?? payment.source}”.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={posting}>
            Cancel
          </Button>
          <Button onClick={post} disabled={posting || !options || !accountId || (mode === "invoice" && !invoiceId) || (mode === "extras" && excess <= 0) || (!!cross && !(fxValue > 0 && fxValue <= (chosen?.amountDue ?? 0) + 0.01)) || (fxOff && !fxConfirmed) || (mode === "invoice" && over > 0 && !writeOff)}>
            {posting ? "Posting…" : mode === "extras" ? "Invoice extras & post to Xero" : writeOff === "underpayment_bank_fees" ? "Post & write off to bank fees" : writeOff === "overpayment_card_fee" ? "Post & invoice the card fee" : writeOff === "overpayment_cogs" ? "Post & write off to COGS" : "Post to Xero"}
          </Button>
        </DialogFooter>
      </DialogContent>
      {finding && (
        <XeroInvoiceSearchDialog
          bookingId={payment.bookingId}
          bookingLabel={payment.clientName}
          brand={payment.brand}
          initialQuery={payment.clientName?.split(/\s+/).pop() ?? payment.counterparty}
          onClose={() => setFinding(false)}
          onLinked={() => { setFinding(false); setOptions(null); loadOptions(); }}
        />
      )}
    </Dialog>
  );
}

/* -------------------- Booking search (match / re-match) -------------------- */

/** Build one-click search terms from a payment: the payer's full name, their
 *  probable surname, charter-code-looking tokens in the reference, and the
 *  reference's distinctive words — so matching rarely needs typing. */
function quickSearchTerms(reference: string | null, counterparty: string | null): string[] {
  const terms: string[] = [];
  const push = (v: string | null | undefined) => {
    const t = (v ?? "").trim();
    if (t.length >= 2 && !terms.some((x) => x.toLowerCase() === t.toLowerCase())) terms.push(t);
  };
  const name = (counterparty ?? "").trim();
  if (name) {
    push(name);
    const words = name.split(/\s+/).filter((w) => /[a-z]/i.test(w));
    if (words.length > 1) push(words[words.length - 1]); // surname
  }
  const ref = reference ?? "";
  // Charter-account-code shapes: 6-hex ("4bd660"), letter+digits ("X9517").
  for (const m of ref.matchAll(/\b(?:[0-9a-f]{5,7}|[A-Za-z]\d{4,7})\b/g)) push(m[0]);
  const NOISE = new Set(["payment", "charter", "invoice", "deposit", "balance", "from", "yacht", "booking", "transfer", "helm", "anchor"]);
  for (const w of ref.split(/[^A-Za-z]+/)) {
    if (w.length >= 4 && !NOISE.has(w.toLowerCase())) push(w);
    if (terms.length >= 8) break;
  }
  return terms.slice(0, 8);
}

function BookingSearchDialog({
  title,
  subtitle,
  endpoint,
  suggestions = [],
  paymentCurrency = null,
  paymentAmount = null,
  onClose,
  onDone,
}: {
  title: string;
  subtitle: string;
  endpoint: string; // POST {bookingId} — approve or rematch
  suggestions?: string[];
  /** Highlights the invoice in the payment's currency under each hit. */
  paymentCurrency?: string | null;
  /** Ranks live deals whose amount is double (50% deposit) or equal to the payment first. */
  paymentAmount?: number | null;
  onClose: () => void;
  /** Called with the server's response (approve / rematch result), the booking id used, and the hit picked — so the caller can open the next step (Allocate / Find invoice) when the money didn't post straight to an invoice. */
  onDone: (result?: any, bookingId?: string, hit?: BookingHit) => void;
}) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<BookingHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setHits(null);
      return;
    }
    const t = setTimeout(() => {
      setSearching(true);
      fetch<{ data: BookingHit[] }>(`/api/finance/bookings-search?q=${encodeURIComponent(q.trim())}${paymentAmount ? `&amount=${encodeURIComponent(String(paymentAmount))}` : ""}${paymentCurrency ? `&currency=${encodeURIComponent(paymentCurrency)}` : ""}`, "GET")
        .then((r) => setHits(r.data ?? []))
        .catch(() => setHits([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const navigate = useNavigate();
  const pick = (b: BookingHit) => {
    setPosting(true);
    setError(null);
    if (b.kind === "ca" && b.ca) {
      const ca = b.ca; const paymentId = /payments-inbox\/([^/]+)\//.exec(endpoint)?.[1];
      if (!ca.hasInvoice) { setPosting(false); toast({ title: "No Xero invoice on the CA booking", description: "Create the Xero records on the CA booking page first, then match here.", variant: "destructive" }); navigate(`/ca/${ca.hsObjectId}?tab=payments`); onClose(); return; }
      if (!paymentId) { setPosting(false); setError("Can't tell which payment this is."); return; }
      fetch<{ data: any }>(`/api/finance/payments-inbox/${paymentId}/ca-approve`, "POST", { hsObjectId: ca.hsObjectId, sequence: ca.sequence, netOfBrokerCommission: !!ca.netOfCommission })
        .then((r: any) => { toast({ title: r?.data?.xeroPaymentId ? "Recorded on the CA booking and posted to Xero" : "Recorded on the CA booking", description: `${b.clientName ?? "Client"} · ${ca.yacht ?? ""} · instalment #${ca.sequence}${r?.data?.xeroError ? ` — Xero: ${r.data.xeroError}` : ""}` }); onDone(r?.data, undefined, b); })
        .catch((e: any) => { setError(e?.message ?? "Failed to record on the CA booking."); setPosting(false); });
      return;
    }
    // A deal with no booking yet: create the booking shell from the deal (and link its invoices), then match.
    const bookingIdP: Promise<string> = b.kind === "deal" && b.hsDealId
      ? fetch<{ data: { bookingId: string; created: boolean } }>("/api/finance/payments-inbox/adopt-deal", "POST", { hsDealId: b.hsDealId }).then((r: any) => { if (!r.data.bookingId) throw new Error(r.data.skipped === "cancelled" ? "That deal is cancelled — not a booking to match money to." : "The deal could not be adopted as a booking."); if (r.data.created) toast({ title: "Booking created from the deal", description: `${b.clientName ?? b.dealName ?? b.hsDealId} now has a booking; its Xero invoices are linked.` }); return r.data.bookingId as string; })
      : Promise.resolve(b.bookingId as string);
    let usedBookingId = "";
    bookingIdP
      .then((bookingId) => { usedBookingId = bookingId; return fetch<{ data: any }>(endpoint, "POST", { bookingId }); })
      .then((r: any) => {
        const x = r?.data?.xero;
        toast({ title: x?.allocated ? "Matched and posted to Xero" : "Matched", description: x?.allocated ? `Paid against ${x.invoiceNumber ?? "the invoice"} from ${x.account}.` : `Payment linked to ${b.clientName ?? "the booking"}${x?.reason ? ` — ${x.reason}` : ""}.` });
        onDone(r?.data, usedBookingId, b);
      })
      .catch((e: any) => {
        setError(e?.message ?? "Failed to match.");
        setPosting(false);
      });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !posting && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">{subtitle}</p>
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <input
          autoFocus
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Client email, booking code, HubSpot id, name, previous reference — or yacht…"
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
        />
        {suggestions.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-400">From the payment:</span>
            {suggestions.map((t) => (
              <button
                key={t}
                onClick={() => setQ(t)}
                className={cn(
                  "rounded-full border px-2 py-0.5 text-xs",
                  q === t ? "border-slate-800 bg-slate-800 text-white" : "bg-white text-slate-600 hover:border-blue-400 hover:text-blue-700",
                )}
              >
                {t}
              </button>
            ))}
          </div>
        )}
        <div className="space-y-2">
          {q.trim().length < 2 ? (
            <p className="text-sm text-slate-400">Type at least two characters to search all bookings.</p>
          ) : searching && hits === null ? (
            <p className="text-sm text-slate-400">Searching…</p>
          ) : hits && hits.length === 0 ? (
            <p className="text-sm text-slate-400">No bookings match "{q}".</p>
          ) : (
            (hits ?? []).map((b) => (
              <button
                key={b.bookingId ?? `deal-${b.hsDealId}`}
                disabled={posting}
                className={cn("flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left hover:border-blue-400 hover:bg-blue-50/50 disabled:opacity-50", b.kind === "deal" && "border-dashed border-amber-300 bg-amber-50/40")}
                onClick={() => pick(b)}
              >
                <span className="min-w-0">
                  <span className="font-medium">{b.clientName ?? b.dealName ?? (b.bookingId ?? b.hsDealId ?? "").slice(0, 8)}</span>
                  {b.fit && <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800" title="The payment fits this deal's amount">{b.fit === "deposit" ? "✓ 50% deposit" : "✓ full amount"}{b.dealAmount != null ? ` of ${fmtMoney(b.dealAmount, b.dealCurrency ?? "EUR")}` : ""}</span>}
                  {b.kind === "ca" && <span className="ml-2 rounded-full bg-teal-100 px-2 py-0.5 text-[10px] font-medium text-teal-800" title="A central-agent booking on HELM's own fleet — selecting records this instalment on it (HubSpot slot + Xero invoice payment)">CA · {b.yachtName ?? "fleet"}</span>}
                  {b.kind === "deal" && (b.invoices
                    ? <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800" title="Won outside the app: its Xero invoices are indexed under the deal but there is no booking row yet. Selecting creates the booking from the deal and links those invoices.">deal · no booking yet</span>
                    : <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-800" title="A HubSpot deal with no booking and no invoice yet. Selecting creates the booking from the deal, matches the payment to it and opens Confirm booking — charter account, invoice, schedule, HubSpot stage.">new deal · select to confirm the booking</span>)}
                  {b.clientEmail && <span className="ml-2 text-xs text-slate-500">{b.clientEmail}</span>}
                  {(b.dealStage || b.ticketStage) && (
                    <span className="mt-0.5 flex flex-wrap items-center gap-1">
                      {b.dealStage && <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium", /contract out|not yet confirmed|qualif|offer|initial/i.test(b.dealStage) ? "bg-violet-100 text-violet-800" : /lost|cancel/i.test(b.dealStage) ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700")} title="HubSpot deal stage">Deal: {b.dealStage}</span>}
                      {b.ticketStage && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600" title="Ops ticket stage">Ticket: {b.ticketStage}</span>}
                    </span>
                  )}
                  <span className="block text-xs text-slate-500">
                    {[b.yachtName, b.accountCode ? `#${b.accountCode}` : null, b.hubspotId ? `HS ${b.hubspotId}` : null, b.brand, b.startDate ? fmtDate(b.startDate) : null, b.kind === "deal" && !b.invoices ? (b.dealName ?? "").replace(/\s+-\s+\d{6,}\s*$/, "").trim() || null : null, (b as any).dealAmount != null ? fmtMoney((b as any).dealAmount, (b as any).dealCurrency ?? "EUR") : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  {b.kind === "ca" && b.ca ? (
                    <span className={cn("block text-[11px]", b.ca.hasInvoice ? "text-teal-800" : "text-amber-700")}>{b.ca.hasInvoice ? (b.ca.netOfCommission ? `Instalment #${b.ca.sequence} of ${b.ca.of} net of the broker's commission (${fmtMoney(b.ca.outstanding, b.ca.currency)} − ${fmtMoney(b.ca.brokerCommission ?? 0, b.ca.currency)}) — marks it received in full${b.ca.broker ? ` · via ${b.ca.broker}` : ""}` : `Records as client instalment #${b.ca.sequence} of ${b.ca.of} · ${fmtMoney(b.ca.outstanding, b.ca.currency)} outstanding${b.ca.broker ? ` · via ${b.ca.broker}` : ""}`) : "No Xero invoice on this CA booking yet — selecting opens its page to create the Xero records"}</span>
                  ) : b.openInvoices?.length ? (
                    <InvoiceTargets invoices={b.openInvoices} target={b.openInvoices.find((i) => !paymentCurrency || !i.currency || i.currency.toUpperCase() === paymentCurrency.toUpperCase()) ?? null} className="mt-0.5" />
                  ) : (
                    <span className={cn("block text-[11px]", b.invoices ? "text-slate-400" : b.kind === "deal" ? "text-violet-700" : "text-amber-700")}>{b.invoices ? "All invoices paid — a payment here is extras / APA" : b.kind === "deal" ? "No invoice yet — this opens Confirm booking to raise it and post the payment" : "No Xero invoice linked — Confirm booking raises it when the deal is unconfirmed, else find it in Xero"}</span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  {b.matchedOn && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{b.matchedOn}</span>}
                  <span className="text-xs font-medium text-blue-600">Select</span>
                </span>
              </button>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={posting} onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ Dismiss ------------------------------ */

function DismissDialog({ payment, onClose, onDone }: { payment: InboxPayment; onClose: () => void; onDone: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [reason, setReason] = useState<"rejected" | "already_in_xero">("rejected");
  const [note, setNote] = useState("");
  const [xeroPaymentId, setXeroPaymentId] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dismiss = () => {
    setPosting(true);
    setError(null);
    fetch(`/api/finance/payments-inbox/${payment.id}/dismiss`, "POST", {
      reason,
      note: note.trim() || undefined,
      xeroPaymentId: reason === "already_in_xero" && xeroPaymentId.trim() ? xeroPaymentId.trim() : undefined,
    })
      .then(() => {
        toast({ title: "Dismissed", description: "The payment moved to the Dismissed tab — restore it from there if needed." });
        onDone();
      })
      .catch((e: any) => {
        setError(e?.message ?? "Failed to dismiss.");
        setPosting(false);
      });
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !posting && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dismiss payment</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          {fmtMoney(payment.amount, payment.currency ?? "EUR")} · {payment.reference ?? payment.counterparty ?? "no reference"}
        </p>
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        <div className="space-y-2">
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" className="mt-1" checked={reason === "rejected"} onChange={() => setReason("rejected")} />
            <span>
              <span className="font-medium">Not a charter payment</span>
              <span className="block text-xs text-slate-500">Test charge, refund, transfer between own accounts, fraud.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="radio" className="mt-1" checked={reason === "already_in_xero"} onChange={() => setReason("already_in_xero")} />
            <span>
              <span className="font-medium">Already recorded in Xero</span>
              <span className="block text-xs text-slate-500">The payment was posted manually — nothing more to do.</span>
            </span>
          </label>
          {reason === "already_in_xero" && (
            <input
              value={xeroPaymentId}
              onChange={(e) => setXeroPaymentId(e.target.value)}
              placeholder="Xero payment id (optional)"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
            />
          )}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            rows={2}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={posting} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={posting} onClick={dismiss}>
            {posting ? "Dismissing…" : "Dismiss"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------ Reviewed tab ------------------------------ */

const REVIEW_LABEL: Record<string, string> = {
  rejected: "Not a charter payment",
  already_in_xero: "Already in Xero",
};

function ReviewedTab({ reviewed, onChanged }: { reviewed: ReviewedPayment[] | null; onChanged: () => void }) {
  const { fetch } = useFetch();
  const readOnly = useReadOnly();
  const { toast } = useToast();
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [notesFor, setNotesFor] = useState<ReviewedPayment | null>(null);

  const restore = (r: ReviewedPayment) => {
    setBusy((b) => ({ ...b, [r.id]: true }));
    fetch(`/api/finance/payments-inbox/${r.id}/restore`, "POST")
      .then(() => {
        toast({ title: "Restored", description: "The payment is back in the To match tab." });
        onChanged();
      })
      .catch((e: any) => toast({ title: "Error", description: e?.message ?? "Failed to restore.", variant: "destructive" }))
      .finally(() => setBusy((b) => ({ ...b, [r.id]: false })));
  };

  return (
    <>
      <div className="overflow-x-auto rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Received</th>
              <th className="px-3 py-2">Reference / payer</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Dismissed by</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {reviewed === null ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>
            ) : reviewed.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Nothing dismissed.</td></tr>
            ) : (
              reviewed.map((r) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="whitespace-nowrap px-3 py-2">
                    <span className="font-medium tabular-nums">{fmtMoney(r.amount, r.currency ?? "EUR")}</span>
                    <span className="block text-xs text-slate-400">
                      <SourcePill source={r.source} bankAccountName={r.bankAccountName} className="mr-1.5" />{fmtDate(r.occurredAt)}
                      <button className={cn("ml-2 hover:text-slate-700 hover:underline", r.noteCount && "font-medium text-slate-600")} onClick={() => setNotesFor(r)}>
                        {r.noteCount ? `${r.noteCount} note${r.noteCount > 1 ? "s" : ""}` : "+ note"}
                      </button>
                    </span>
                    <PaymentMeta status={r.status} cardLabel={r.cardLabel} card={r.card} />
                  </td>
                  <td className="max-w-[280px] px-3 py-2 text-slate-600">
                    <span className="line-clamp-2">{r.reference ?? "—"}</span>
                    {r.counterparty && <span className="block text-xs text-slate-400">{r.counterparty}</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                      {REVIEW_LABEL[r.reviewStatus] ?? r.reviewStatus}
                    </span>
                    {r.reviewNote && <span className="mt-1 block max-w-[240px] text-xs text-slate-500">{r.reviewNote}</span>}
                    {r.xeroPaymentId && <span className="mt-1 block text-xs text-slate-400">Xero payment {r.xeroPaymentId.slice(0, 8)}…</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">
                    {r.reviewedBy ?? "—"}
                    {r.reviewedAt && <span className="block">{fmtDate(r.reviewedAt)}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    {!readOnly && <button className="text-sm text-blue-600 hover:underline disabled:opacity-50" disabled={!!busy[r.id]} onClick={() => restore(r)}>
                      Restore
                    </button>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">
        Dismissed payments never re-enter the inbox on sync. Restore puts one back in To match.
      </p>
      {notesFor && (
        <NotesDialog
          paymentId={notesFor.id}
          label={`${fmtMoney(notesFor.amount, notesFor.currency ?? "EUR")} · ${notesFor.reference ?? notesFor.counterparty ?? ""}`}
          onClose={() => setNotesFor(null)}
          onChanged={onChanged}
        />
      )}
    </>
  );
}

/* ------------------------------- Notes ------------------------------- */

type PaymentNoteRow = { id: string; authorEmail: string | null; body: string; createdAt: string | null; fromOps: boolean };

function NotesDialog({ paymentId, label, onClose, onChanged }: { paymentId: string; label: string; onClose: () => void; onChanged: () => void }) {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const [notes, setNotes] = useState<PaymentNoteRow[] | null>(null);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    fetch<{ data: PaymentNoteRow[] }>(`/api/finance/payments-inbox/${paymentId}/notes`, "GET")
      .then((r) => setNotes(r.data ?? []))
      .catch(() => setNotes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);
  useEffect(load, [load]);

  const close = () => {
    if (dirty) onChanged();
    onClose();
  };

  const add = () => {
    if (!draft.trim()) return;
    setPosting(true);
    fetch(`/api/finance/payments-inbox/${paymentId}/notes`, "POST", { body: draft.trim() })
      .then(() => {
        setDraft("");
        setDirty(true);
        load();
      })
      .catch((e: any) => toast({ title: "Error", description: e?.message ?? "Failed to add the note.", variant: "destructive" }))
      .finally(() => setPosting(false));
  };

  const remove = (n: PaymentNoteRow) => {
    fetch(`/api/finance/payment-notes/${n.id}/delete`, "POST")
      .then(() => {
        setDirty(true);
        load();
      })
      .catch((e: any) => toast({ title: "Error", description: e?.message ?? "Failed to delete the note.", variant: "destructive" }));
  };

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[80vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Notes</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">{label}</p>
        <div className="space-y-2">
          {notes === null ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : notes.length === 0 ? (
            <p className="text-sm text-slate-400">No notes yet.</p>
          ) : (
            notes.map((n) => (
              <div key={n.id} className="rounded-lg bg-slate-50 px-3 py-2">
                <p className="whitespace-pre-wrap text-sm text-slate-800">{n.body}</p>
                <p className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                  {n.authorEmail ?? "—"} · {fmtDate(n.createdAt)}
                  {n.fromOps && <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600">ops</span>}
                  {!n.fromOps && (
                    <button className="text-slate-400 hover:text-red-600 hover:underline" onClick={() => remove(n)}>
                      delete
                    </button>
                  )}
                </p>
              </div>
            ))
          )}
        </div>
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a note…"
            rows={2}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
          />
          <Button disabled={posting || !draft.trim()} onClick={add}>
            {posting ? "…" : "Add"}
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
